import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatIDR } from "../lib/format";
import { toast } from "sonner";
import { Users, Store as StoreIcon, ShieldCheck, Settings, AlertTriangle, BarChart3, Trash2, Send, Plus, Gavel, Package, Inbox, Search, X, CreditCard, Wallet, Ticket, Star, Gift, Zap } from "lucide-react";
import AdminReferral from "./AdminReferral";
import AdminFlashSale from "./AdminFlashSale";
import AdminInstantProducts from "./AdminInstantProducts";



const TABS = [
  { id: "stats", label: "Utama", icon: BarChart3 },
  { id: "config", label: "Konfigurasi", icon: Settings },
  { id: "admins", label: "Kelola Admin", icon: ShieldCheck },
  { id: "buyers", label: "Kelola Buyer", icon: Users },
  { id: "sellers", label: "Kelola Seller", icon: StoreIcon },
  { id: "flash-sale", label: "Flash Sale", icon: Zap },
  { id: "instant", label: "Produk Instan", icon: Zap },
  { id: "products", label: "Kelola Produk", icon: Package },

  { id: "product-detail", label: "Detail Produk Seller", icon: Search },
  { id: "auctions", label: "Kelola Auction", icon: Gavel },
  { id: "auction-detail", label: "Detail Produk Auction", icon: Gavel },
  { id: "payments", label: "Kelola Pembayaran", icon: CreditCard },
  { id: "topups", label: "Kelola Topup", icon: Wallet },
  { id: "vouchers", label: "Kelola Voucher", icon: Ticket },
  { id: "withdrawals", label: "Penarikan Seller", icon: Wallet },
  { id: "reviews", label: "Kelola Review", icon: Star },
  { id: "disputes", label: "Komplain Order", icon: AlertTriangle },
  { id: "requests", label: "Request Produk", icon: Inbox },
  { id: "referral", label: "Program Referal", icon: Gift },
  { id: "reports", label: "Pengaduan", icon: AlertTriangle },

];

const SUB_CATEGORY_OPTIONS = [
  { value: "akun", label: "Akun" },
  { value: "topup_via_id", label: "Top-up via ID" },
  { value: "topup_via_login", label: "Top-up via Login" },
  { value: "voucher", label: "Voucher" },
  { value: "joki", label: "Joki" },
];

const ROOT_ADMIN_EMAIL = "admin@ustore.com";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("stats");
  const [isAdminUser, setIsAdminUser] = useState(null);

  useEffect(() => {
    if (user && user !== false) {
      api.get("/admin/stats").then(() => setIsAdminUser(true)).catch(() => setIsAdminUser(false));
    }
  }, [user]);

  if (loading || isAdminUser === null) return <div className="text-white/50 text-center py-32">Loading...</div>;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (!isAdminUser) return <div className="max-w-md mx-auto px-4 py-24 text-center"><h1 className="font-display font-black text-2xl text-[#FF003C]">403 - Admin Only</h1><p className="text-white/60 mt-2">Akses ditolak. Hanya admin yang bisa masuk dashboard ini.</p></div>;

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-4 md:px-8 py-12">
      <span className="tag" style={{ borderColor: "rgba(255,0,60,0.5)", color: "#FF003C", background: "rgba(255,0,60,0.1)" }}>// ADMIN COMMAND</span>
      <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white mt-3 mb-2">Admin Dashboard</h1>
      <p className="text-white/55 mb-8">{user.email}</p>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        <aside className="border border-white/10 p-2 h-fit">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)} className={`w-full flex items-center gap-2 px-3 py-3 text-xs uppercase font-bold tracking-widest ${tab === t.id ? "bg-[#FF003C]/10 text-[#FF003C]" : "text-white/60 hover:bg-white/5"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </aside>

        <div>
          {tab === "stats" && <StatsTab />}
          {tab === "config" && <ConfigTab />}
          {tab === "admins" && <AdminsTab />}
          {tab === "buyers" && <UsersTab role="buyer" />}
          {tab === "sellers" && <UsersTab role="seller" />}
          {tab === "flash-sale" && <AdminFlashSale />}
          {tab === "instant" && <AdminInstantProducts />}
          {tab === "products" && <ProductCategoriesTab />}

          {tab === "product-detail" && <ProductDetailTab />}
          {tab === "auctions" && <AuctionGamesTab />}
          {tab === "auction-detail" && <AuctionDetailTab />}
          {tab === "payments" && <PaymentMethodsTab />}
          {tab === "topups" && <TopupHistoryTab />}
          {tab === "vouchers" && <VouchersTab />}
          {tab === "withdrawals" && <WithdrawalsTab />}
          {tab === "reviews" && <ReviewsTab />}
          {tab === "disputes" && <DisputesTab />}
          {tab === "requests" && <ProductRequestsTab />}
          {tab === "referral" && <AdminReferral />}
          {tab === "reports" && <ReportsTab />}

        </div>
      </div>
    </div>
  );
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState("today");
  useEffect(() => { api.get(`/admin/stats?period=${period}`).then((r) => setStats(r.data)); }, [period]);
  if (!stats) return <p className="text-white/50">Loading...</p>;
  const cards = [
    { l: "Total Admin", v: stats.total_admins, c: "#FF003C" },
    { l: "Total Seller", v: stats.total_sellers, c: "#00F0FF" },
    { l: "Total Buyer", v: stats.total_buyers, c: "#FFD700" },
    { l: "Total Produk", v: stats.total_products, c: "#00FF66" },
    { l: "Total Penjualan", v: formatIDR(stats.total_sales), c: "#00F0FF" },
    { l: "Omset Bersih (5%)", v: formatIDR(stats.net_omset), c: "#00FF66" },
  ];
  return (
    <div>
      <div className="flex gap-2 mb-6">
        <button data-testid="period-today" onClick={() => setPeriod("today")} className={`px-4 py-2 text-xs uppercase font-bold tracking-widest border ${period === "today" ? "border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-white/70"}`}>Hari ini</button>
        <button data-testid="period-month" onClick={() => setPeriod("month")} className={`px-4 py-2 text-xs uppercase font-bold tracking-widest border ${period === "month" ? "border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-white/70"}`}>30 Hari</button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((c) => (
          <div key={c.l} className="border border-white/10 p-5" data-testid={`stat-${c.l.replace(/ /g, "-").toLowerCase()}`}>
            <div className="font-display font-black text-2xl" style={{ color: c.c }}>{c.v}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">{c.l}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 text-xs text-white/40 uppercase tracking-widest">{stats.orders_count} order · {stats.auction_count} auction · sejak {stats.since?.slice(0, 19)}</div>
    </div>
  );
}

function ConfigTab() {
  const [form, setForm] = useState({});
  useEffect(() => { api.get("/admin/config").then((r) => setForm(r.data)); }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/config", form);
      setForm(data);
      toast.success("Config tersimpan");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <form onSubmit={save} className="border border-white/10 p-6 space-y-4" data-testid="config-form">
      {[
        { k: "site_name", l: "Nama Website" },
        { k: "site_title", l: "Judul Web (browser tab)" },
        { k: "site_description", l: "Deskripsi Web", textarea: true },
        { k: "logo_url", l: "Upload Logo (URL)" },
        { k: "store_code", l: "Kode Store (mis. UST)" },
      ].map((f) => (
        <div key={f.k}>
          <label className="text-xs uppercase tracking-[0.2em] text-white/60">{f.l}</label>
          {f.textarea ? (
            <textarea data-testid={`config-${f.k}`} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} rows={3} className="w-full bg-black border border-white/15 focus:border-[#00F0FF] text-white text-sm p-3 mt-2" />
          ) : (
            <input data-testid={`config-${f.k}`} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="w-full bg-black border border-white/15 focus:border-[#00F0FF] text-white text-sm p-3 mt-2" />
          )}
        </div>
      ))}
      <button type="submit" data-testid="config-save" className="btn-primary">Simpan Config</button>
      <p className="text-[10px] text-white/40 uppercase tracking-widest">Kode Store dipakai sebagai prefix order ID seluruh transaksi.</p>
    </form>
  );
}

function AdminsTab() {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const refresh = () => api.get("/admin/admins").then((r) => setAdmins(r.data));
  useEffect(() => { refresh(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/admins", form);
      toast.success("Admin ditambahkan");
      setForm({ email: "", name: "", password: "" });
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Hapus admin ini?")) return;
    await api.delete(`/admin/admins/${id}`);
    toast.success("Dihapus");
    refresh();
  };
  return (
    <div>
      <form onSubmit={add} className="border border-white/10 p-4 grid md:grid-cols-4 gap-3 mb-6" data-testid="add-admin-form">
        <input data-testid="admin-name" required placeholder="Nama" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black border border-white/15 p-2 text-sm" />
        <input data-testid="admin-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-black border border-white/15 p-2 text-sm" />
        <input data-testid="admin-password" required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-black border border-white/15 p-2 text-sm" />
        <button type="submit" className="btn-primary text-xs"><Plus className="inline w-3 h-3 mr-1" /> Tambah Admin</button>
      </form>
      <div className="space-y-2">
        <div className="border border-white/10 p-3 flex items-center gap-4 bg-[#FF003C]/5">
          <div className="font-display font-black text-[#FF003C]">★ ROOT</div>
          <div className="flex-1"><p className="font-bold text-white text-sm">Ustore Admin</p><p className="text-xs text-white/50">{ROOT_ADMIN_EMAIL} · root admin</p></div>
        </div>
        {admins.map((a) => (
          <div key={a.id} className="border border-white/10 p-3 flex items-center gap-4" data-testid={`admin-${a.id}`}>
            <ShieldCheck className="w-4 h-4 text-[#FF003C]" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-white text-sm">{a.name}</p>
              <p className="text-xs text-white/50">{a.email} · daftar {new Date(a.created_at).toLocaleString("id-ID")}</p>
            </div>
            <button onClick={() => remove(a.id)} className="text-white/60 hover:text-[#FF003C] p-2"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersTab({ role }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [creating, setCreating] = useState(null);
  const search = async () => {
    const { data } = await api.get(`/admin/users?role=${role}&q=${encodeURIComponent(q)}`);
    setUsers(data);
  };
  useEffect(() => { search(); }, [role]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    try {
      await api.put(`/admin/users/${editing.id}`, {
        name: editing.name, shop_name: editing.shop_name, phone: editing.phone, status: editing.status,
        set_balance: editing._setBalance !== undefined ? parseFloat(editing._setBalance) : undefined,
        is_seller: role === "buyer" ? (editing.is_seller || false) : undefined,
      });
      toast.success("Tersimpan");
      setEditing(null);
      search();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const sendCredit = async () => {
    if (!creditAmount || parseFloat(creditAmount) === 0) return;
    try {
      await api.post(`/admin/users/${editing.id}/wallet-credit`, { amount: parseFloat(creditAmount) });
      toast.success(`Saldo ${formatIDR(parseFloat(creditAmount))} dikirim`);
      setCreditAmount("");
      search();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm(`Hapus akun ${role} ini?`)) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("Dihapus");
    search();
  };
  const createUser = async () => {
    if (!creating?.email || !creating?.password || !creating?.name) {
      toast.error("Email, password, dan nama wajib diisi");
      return;
    }
    if (role === "seller" && !creating?.shop_name) {
      toast.error("Nama toko wajib untuk seller");
      return;
    }
    try {
      await api.post("/admin/users", {
        email: creating.email.trim(),
        password: creating.password,
        name: creating.name.trim(),
        role,
        shop_name: role === "seller" ? creating.shop_name.trim() : null,
        phone: creating.phone || null,
        initial_balance: creating.initial_balance ? parseFloat(creating.initial_balance) : 0,
      });
      toast.success(`${role === "seller" ? "Seller" : "Buyer"} berhasil dibuat`);
      setCreating(null);
      search();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal membuat user"); }
  };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        <input data-testid={`${role}-search`} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder={role === "buyer" ? "Cari username/email" : "Cari nama toko/email"} className="flex-1 bg-black border border-white/15 focus:border-[#00F0FF] text-white text-sm px-3 py-2" />
        <button onClick={search} className="btn-primary text-xs">Cari</button>
        <button data-testid={`add-${role}-btn`} onClick={() => setCreating({ email: "", password: "", name: "", shop_name: "", phone: "", initial_balance: "" })} className="text-xs px-4 py-2 bg-[#00FF66] text-black font-bold uppercase tracking-widest hover:bg-[#33FF88]">+ Buat {role === "buyer" ? "Buyer" : "Seller"}</button>
      </div>
      <div className="space-y-2">
        {users.length === 0 && <p className="text-white/50 text-center py-8">Tidak ada {role}.</p>}
        {users.map((u) => (
          <div key={u.id} className="border border-white/10 p-3 flex items-center gap-3" data-testid={`${role}-${u.id}`}>
            <div className="w-10 h-10 bg-[#00F0FF]/10 border border-[#00F0FF]/40 flex items-center justify-center font-bold text-[#00F0FF]">{(u.name || "?")[0].toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-white text-sm">{u.shop_name || u.name}</p>
              <p className="text-xs text-white/50 truncate">{u.email} · saldo {formatIDR(u.balance)} · locked {formatIDR(u.locked_balance)}</p>
            </div>
            <button data-testid={`edit-${u.id}`} onClick={() => setEditing({ ...u })} className="text-xs px-3 py-2 border border-white/15 hover:border-[#00F0FF] text-white/70 hover:text-[#00F0FF] uppercase tracking-widest">Edit</button>
            <button onClick={() => del(u.id)} className="text-white/60 hover:text-[#FF003C] p-2"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

       {editing && (
         <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="edit-modal">
           <div className="w-full max-w-md border border-[#00F0FF]/40 bg-[#0a0a0a] p-6 space-y-3 max-h-[90vh] overflow-y-auto">
             <h3 className="font-display font-bold text-xl text-white">Edit {role}</h3>
             <input placeholder="Username/Nama" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm" />
             {role === "seller" && <input placeholder="Nama Toko" value={editing.shop_name || ""} onChange={(e) => setEditing({ ...editing, shop_name: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm" />}
             <input placeholder="No. WA" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm" />
             <select value={editing.status || "active"} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm text-white">
               <option value="active">Active</option>
               <option value="suspended">Suspended</option>
             </select>
             {role === "buyer" && (
               <div className="border border-[#00FF66]/30 bg-[#00FF66]/5 p-3 rounded">
                 <label className="flex items-center gap-2 cursor-pointer">
                   <input
                     type="checkbox"
                     checked={editing.is_seller || false}
                     onChange={(e) => setEditing({ ...editing, is_seller: e.target.checked })}
                     className="w-4 h-4 cursor-pointer"
                     data-testid="buyer-to-seller-toggle"
                   />
                   <span className="text-xs uppercase tracking-widest text-[#00FF66] font-bold">Jadikan Seller Juga</span>
                 </label>
                 <p className="text-[10px] text-white/50 mt-2">Jika diaktifkan, user akan memiliki role buyer DAN seller (dual role)</p>
               </div>
             )}
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Set Saldo (replace)</label>
              <input type="number" placeholder={`${editing.balance}`} value={editing._setBalance ?? ""} onChange={(e) => setEditing({ ...editing, _setBalance: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm mt-1" />
            </div>
            <div className="border-t border-white/10 pt-3">
              <label className="text-xs uppercase tracking-widest text-white/60">Kirim Saldo (tambah/kurang)</label>
              <div className="flex gap-2 mt-1">
                <input type="number" placeholder="Rp" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} className="flex-1 bg-black border border-white/15 p-2 text-sm" />
                <button onClick={sendCredit} className="px-3 py-2 bg-[#00FF66] text-black text-xs uppercase font-bold tracking-widest"><Send className="inline w-3 h-3 mr-1" /> Kirim</button>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={save} className="flex-1 btn-primary text-xs">Simpan</button>
              <button onClick={() => setEditing(null)} className="px-4 py-2 border border-white/15 text-white/70 text-xs uppercase">Tutup</button>
            </div>
          </div>
        </div>
      )}

      {role === "seller" && <SellerFeesAndGames />}

      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid={`create-${role}-modal`}>
          <div className="w-full max-w-md border border-[#00FF66]/40 bg-[#0a0a0a] p-6 space-y-3 max-h-[90vh] overflow-y-auto">
            <h3 className="font-display font-bold text-xl text-white">Buat {role === "buyer" ? "Buyer" : "Seller"} Baru</h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Email *</label>
              <input data-testid={`create-${role}-email`} type="email" placeholder="user@example.com" value={creating.email} onChange={(e) => setCreating({ ...creating, email: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Password * (min 6 karakter)</label>
              <input data-testid={`create-${role}-password`} type="text" placeholder="password" value={creating.password} onChange={(e) => setCreating({ ...creating, password: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Nama / Username *</label>
              <input data-testid={`create-${role}-name`} placeholder="Nama lengkap" value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm mt-1" />
            </div>
            {role === "seller" && (
              <div>
                <label className="text-xs uppercase tracking-widest text-white/60">Nama Toko *</label>
                <input data-testid="create-seller-shop" placeholder="undeadshopp" value={creating.shop_name} onChange={(e) => setCreating({ ...creating, shop_name: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm mt-1" />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">No. WhatsApp (opsional)</label>
              <input placeholder="08xxxxxxxxx" value={creating.phone} onChange={(e) => setCreating({ ...creating, phone: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Saldo Awal (opsional)</label>
              <input type="number" placeholder="0" value={creating.initial_balance} onChange={(e) => setCreating({ ...creating, initial_balance: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm mt-1" />
            </div>
            <p className="text-[10px] text-white/40">Catatan: user akan langsung bisa login pakai email & password ini. Sampaikan kredensial ke user yang bersangkutan.</p>
            <div className="flex gap-2 pt-2">
              <button data-testid={`create-${role}-submit`} onClick={createUser} className="flex-1 px-4 py-2 bg-[#00FF66] text-black text-xs uppercase font-bold tracking-widest hover:bg-[#33FF88]">Buat Akun</button>
              <button onClick={() => setCreating(null)} className="px-4 py-2 border border-white/15 text-white/70 text-xs uppercase">Batal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SellerFeesAndGames() {
  const [fees, setFees] = useState([]);
  const [games, setGames] = useState([]);
  const [feeForm, setFeeForm] = useState({ game: "", fee_pct: 3 });
  const [gameForm, setGameForm] = useState({ id: "", name: "", image: "" });
  const refresh = () => {
    api.get("/admin/seller-fees").then((r) => setFees(r.data));
    api.get("/admin/games").then((r) => setGames(r.data));
  };
  useEffect(() => { refresh(); }, []);
  const saveFee = async (e) => {
    e.preventDefault();
    await api.post("/admin/seller-fees", { game: feeForm.game, fee_pct: parseFloat(feeForm.fee_pct) });
    toast.success("Fee tersimpan");
    setFeeForm({ game: "", fee_pct: 3 });
    refresh();
  };
  const saveGame = async (e) => {
    e.preventDefault();
    await api.post("/admin/games", gameForm);
    toast.success("Game ditambahkan");
    setGameForm({ id: "", name: "", image: "" });
    refresh();
  };
  return (
    <div className="mt-8 border-t border-white/10 pt-6 space-y-6">
      <div>
        <h3 className="font-display font-bold text-lg text-white mb-3">Fee Seller per Game</h3>
        <form onSubmit={saveFee} className="flex flex-wrap gap-2 mb-3" data-testid="fee-form">
          <input data-testid="fee-game" required placeholder="game id (mis. mobile-legends)" value={feeForm.game} onChange={(e) => setFeeForm({ ...feeForm, game: e.target.value })} className="bg-black border border-white/15 p-2 text-sm flex-1 min-w-[200px]" />
          <input data-testid="fee-pct" required type="number" step="0.1" placeholder="%" value={feeForm.fee_pct} onChange={(e) => setFeeForm({ ...feeForm, fee_pct: e.target.value })} className="bg-black border border-white/15 p-2 text-sm w-24" />
          <button type="submit" className="btn-primary text-xs">Set Fee</button>
        </form>
        <div className="space-y-1">
          {fees.map((f) => (
            <div key={f.game} className="flex justify-between border border-white/10 px-3 py-2 text-sm">
              <span className="text-white">{f.game}</span>
              <span className="text-[#FFD700] font-bold">{f.fee_pct}%</span>
              <button onClick={async () => { await api.delete(`/admin/seller-fees/${f.game}`); refresh(); }} className="text-white/40 hover:text-[#FF003C]"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-display font-bold text-lg text-white mb-3">Tambah Game / Kategori</h3>
        <form onSubmit={saveGame} className="grid md:grid-cols-4 gap-2" data-testid="game-form">
          <input data-testid="game-id" required placeholder="id (mis. blood-strike)" value={gameForm.id} onChange={(e) => setGameForm({ ...gameForm, id: e.target.value })} className="bg-black border border-white/15 p-2 text-sm" />
          <input data-testid="game-name" required placeholder="Nama Game" value={gameForm.name} onChange={(e) => setGameForm({ ...gameForm, name: e.target.value })} className="bg-black border border-white/15 p-2 text-sm" />
          <input data-testid="game-image" placeholder="URL gambar" value={gameForm.image} onChange={(e) => setGameForm({ ...gameForm, image: e.target.value })} className="bg-black border border-white/15 p-2 text-sm" />
          <button type="submit" className="btn-primary text-xs">+ Game</button>
        </form>
        <p className="text-[10px] text-white/40 uppercase tracking-widest mt-2">Default sub_categories: akun, topup_via_id, topup_via_login, voucher, joki</p>
        <div className="space-y-1 mt-3">
          {games.map((g) => (
            <div key={g.id} className="flex items-center gap-3 border border-white/10 px-3 py-2 text-sm">
              {g.image && <img src={g.image} alt="" className="w-8 h-8 object-cover" />}
              <span className="text-white flex-1">{g.name} <span className="text-white/40 text-xs">({g.id})</span></span>
              <span className="text-[10px] text-white/40">{(g.sub_categories || []).join(", ")}</span>
              <button onClick={async () => { await api.delete(`/admin/games/${g.id}`); refresh(); }} className="text-white/40 hover:text-[#FF003C]"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReportsTab() {
  const [reports, setReports] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const refresh = () => api.get("/reports").then((r) => setReports(r.data));
  useEffect(() => { refresh(); }, []);
  const openReport = async (r) => {
    const { data } = await api.get(`/reports/${r.id}`);
    setActive(data);
  };
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    await api.post(`/reports/${active.id}/messages`, { message: text });
    setText("");
    openReport(active);
  };
  const close = async () => {
    if (!window.confirm("Tutup pengaduan ini?")) return;
    await api.post(`/reports/${active.id}/close`);
    toast.success("Closed");
    refresh();
    setActive(null);
  };
  return (
    <div className="grid md:grid-cols-[260px_1fr] gap-4 border border-white/10 min-h-[60vh]">
      <aside className="border-r border-white/10 overflow-y-auto max-h-[60vh]">
        {reports.length === 0 ? <p className="text-white/50 text-sm p-4">Belum ada pengaduan.</p> : reports.map((r) => (
          <button key={r.id} data-testid={`report-${r.id}`} onClick={() => openReport(r)} className={`w-full text-left p-3 border-b border-white/10 ${active?.id === r.id ? "bg-[#FF003C]/10" : "hover:bg-white/5"}`}>
            <div className="flex justify-between items-center mb-1">
              <span className="font-bold text-white text-sm truncate flex-1">{r.topic}</span>
              <span className={`tag ${r.status === "closed" ? "!border-white/30 !text-white/50" : "!border-[#FF003C]/50 !text-[#FF003C] !bg-[#FF003C]/10"}`}>{r.status}</span>
            </div>
            <p className="text-xs text-white/40 truncate">{r.last_message?.message || "—"}</p>
          </button>
        ))}
      </aside>
      <div className="flex flex-col p-4">
        {!active ? <p className="text-white/40 text-center pt-12">Pilih pengaduan</p> : (
          <>
            <div className="flex justify-between items-center mb-3 pb-3 border-b border-white/10">
              <div>
                <h3 className="font-display font-bold text-white">{active.topic}</h3>
                <p className="text-[10px] uppercase tracking-widest text-white/40">Status: {active.status}</p>
              </div>
              {active.status === "open" && <button onClick={close} className="text-xs px-3 py-2 border border-white/15 hover:border-[#FF003C] text-white/70 hover:text-[#FF003C] uppercase tracking-widest">Tutup Tiket</button>}
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 max-h-[40vh]" data-testid="report-messages">
              {active.messages?.map((m) => (
                <div key={m.id} className={`p-3 border ${m.sender_role === "admin" ? "border-[#FF003C]/40 bg-[#FF003C]/5" : m.sender_role === "seller" ? "border-[#00F0FF]/40 bg-[#00F0FF]/5" : "border-[#FFD700]/40 bg-[#FFD700]/5"}`}>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{m.sender_name} · {m.sender_role}</div>
                  <p className="text-sm text-white">{m.message}</p>
                </div>
              ))}
            </div>
            <form onSubmit={send} className="mt-3 flex gap-2" data-testid="report-form">
              <input data-testid="report-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Tulis balasan..." className="flex-1 bg-black border border-white/15 focus:border-[#FF003C] text-white text-sm px-3 py-2" />
              <button type="submit" className="btn-primary text-xs"><Send className="inline w-3 h-3 mr-1" /> Kirim</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}


function AuctionGamesTab() {
  const [games, setGames] = useState([]);
  const [form, setForm] = useState({ game_id: "", game_name: "", sub_categories: ["akun"], image_url: "", allowed: true });
  const [editing, setEditing] = useState(null);

  const refresh = () => api.get("/admin/auction-games").then((r) => setGames(r.data));
  useEffect(() => { refresh(); }, []);

  const toggleSub = (sub) => {
    const t = editing || form;
    const setT = editing ? setEditing : setForm;
    const cur = t.sub_categories || [];
    setT({ ...t, sub_categories: cur.includes(sub) ? cur.filter((s) => s !== sub) : [...cur, sub] });
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = editing || form;
    if (!payload.game_id || !payload.game_name) { toast.error("Game ID & nama wajib"); return; }
    if (!payload.sub_categories.length) { toast.error("Pilih minimal 1 kategori"); return; }
    try {
      await api.post("/admin/auction-games", payload);
      toast.success(editing ? "Game diupdate" : "Game ditambahkan");
      setForm({ game_id: "", game_name: "", sub_categories: ["akun"], image_url: "", allowed: true });
      setEditing(null);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (gid) => {
    if (!window.confirm("Hapus game ini dari whitelist auction?")) return;
    await api.delete(`/admin/auction-games/${gid}`);
    toast.success("Dihapus");
    refresh();
  };

  const toggleAllow = async (g) => {
    await api.post("/admin/auction-games", { ...g, allowed: !g.allowed });
    toast.success(g.allowed ? "Game di-block" : "Game diizinkan");
    refresh();
  };

  const target = editing || form;
  return (
    <div data-testid="auction-games-tab">
      <div className="border border-white/10 p-5 mb-6">
        <h3 className="font-display font-bold text-lg text-white mb-1">{editing ? "Edit Game Auction" : "Tambah Game Auction"}</h3>
        <p className="text-xs text-white/50 mb-4">Hanya game yang ditambahkan di sini yang bisa dipilih seller saat membuat auction. Toggle "Block" untuk melarang sementara tanpa hapus.</p>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-3" data-testid="auction-game-form">
          <input data-testid="ag-id" required placeholder="game id (mis. mobile-legends)" value={target.game_id} onChange={(e) => editing ? setEditing({ ...editing, game_id: e.target.value }) : setForm({ ...form, game_id: e.target.value })} disabled={!!editing} className="bg-black border border-white/15 p-2 text-sm disabled:opacity-50" />
          <input data-testid="ag-name" required placeholder="Nama Game (mis. Mobile Legends)" value={target.game_name} onChange={(e) => editing ? setEditing({ ...editing, game_name: e.target.value }) : setForm({ ...form, game_name: e.target.value })} className="bg-black border border-white/15 p-2 text-sm" />
          <input data-testid="ag-image" placeholder="URL gambar (opsional)" value={target.image_url || ""} onChange={(e) => editing ? setEditing({ ...editing, image_url: e.target.value }) : setForm({ ...form, image_url: e.target.value })} className="md:col-span-2 bg-black border border-white/15 p-2 text-sm" />
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-white/60">Kategori Yang Diizinkan</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {SUB_CATEGORY_OPTIONS.map((s) => {
                const active = (target.sub_categories || []).includes(s.value);
                return (
                  <button type="button" key={s.value} data-testid={`ag-sub-${s.value}`} onClick={() => toggleSub(s.value)} className={`px-3 py-2 text-xs uppercase tracking-widest border ${active ? "border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-white/60 hover:border-white/40"}`}>
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
          <label className="md:col-span-2 flex items-center gap-2 text-xs text-white/70 cursor-pointer">
            <input type="checkbox" data-testid="ag-allowed" checked={!!target.allowed} onChange={(e) => editing ? setEditing({ ...editing, allowed: e.target.checked }) : setForm({ ...form, allowed: e.target.checked })} className="accent-[#00F0FF]" />
            Diizinkan (uncheck untuk block sementara)
          </label>
          <div className="md:col-span-2 flex gap-2">
            <button data-testid="ag-save" type="submit" className="btn-primary text-xs"><Plus className="inline w-3 h-3 mr-1" /> {editing ? "Update" : "Tambah"} Game</button>
            {editing && <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-white/15 text-white/70 text-xs uppercase tracking-widest">Batal</button>}
          </div>
        </form>
      </div>

      <div className="space-y-2">
        {games.length === 0 && <p className="text-white/50 text-center py-8">Belum ada game di whitelist auction.</p>}
        {games.map((g) => (
          <div key={g.game_id} className={`border ${g.allowed ? "border-white/10" : "border-[#FF003C]/30 bg-[#FF003C]/5"} p-3 flex items-center gap-3`} data-testid={`ag-${g.game_id}`}>
            {g.image_url && <img src={g.image_url} alt="" className="w-12 h-12 object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-bold text-white text-sm">{g.game_name}</p>
                {!g.allowed && <span className="tag" style={{ borderColor: "rgba(255,0,60,0.6)", color: "#FF003C", background: "rgba(255,0,60,0.1)" }}>BLOCKED</span>}
              </div>
              <p className="text-[11px] text-white/50">{g.game_id} · {(g.sub_categories || []).map((s) => SUB_CATEGORY_OPTIONS.find((o) => o.value === s)?.label || s).join(", ")}</p>
            </div>
            <button data-testid={`ag-toggle-${g.game_id}`} onClick={() => toggleAllow(g)} className={`text-xs px-3 py-2 border uppercase tracking-widest ${g.allowed ? "border-[#FF003C]/40 text-[#FF003C] hover:bg-[#FF003C]/10" : "border-[#00FF66]/40 text-[#00FF66] hover:bg-[#00FF66]/10"}`}>{g.allowed ? "Block" : "Allow"}</button>
            <button data-testid={`ag-edit-${g.game_id}`} onClick={() => setEditing({ ...g })} className="text-xs px-3 py-2 border border-white/15 hover:border-[#00F0FF] text-white/70 hover:text-[#00F0FF] uppercase tracking-widest">Edit</button>
            <button onClick={() => remove(g.game_id)} className="text-white/60 hover:text-[#FF003C] p-2"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}


function ProductCategoriesTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ id: "", name: "", image: "", fee_pct: 5, sort_order: 0 });
  const [editing, setEditing] = useState(null);
  const [editingTypes, setEditingTypes] = useState(null); // For game type restrictions

  // Product Types state
  const [types, setTypes] = useState([]);
  const [typeForm, setTypeForm] = useState({ id: "", label: "", description: "", active: true, warranty_days: 3 });
  const [editingTypeFields, setEditingTypeFields] = useState(null); // For editing instant delivery fields

  const refresh = async () => {
    const [games, fees, tps] = await Promise.all([
      api.get("/admin/games").then((r) => r.data),
      api.get("/admin/seller-fees").then((r) => r.data),
      api.get("/admin/product-types").then((r) => r.data),
    ]);
    const feeMap = Object.fromEntries(fees.map((f) => [f.game.toLowerCase(), f.fee_pct]));
    setItems(games.map((g) => ({ ...g, fee_pct: feeMap[g.id.toLowerCase()] ?? 0 })));
    setTypes(tps);
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveType = async (e) => {
    e.preventDefault();
    if (!typeForm.id.trim() || !typeForm.label.trim()) { toast.error("ID & label wajib"); return; }
    try {
      await api.post("/admin/product-types", { ...typeForm, id: typeForm.id.toLowerCase().replace(/\s+/g, "_"), warranty_days: parseInt(typeForm.warranty_days || 3, 10) });
      toast.success("Tipe tersimpan");
      setTypeForm({ id: "", label: "", description: "", active: true, warranty_days: 3 });
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const toggleType = async (t) => {
    await api.post("/admin/product-types", { ...t, active: !t.active });
    toast.success(t.active ? "Tipe di-nonaktifkan" : "Tipe diaktifkan");
    refresh();
  };

  const removeType = async (t) => {
    if (!window.confirm(`Hapus tipe "${t.label}"?`)) return;
    await api.delete(`/admin/product-types/${t.id}`);
    toast.success("Dihapus");
    refresh();
  };

  const toggleGameType = (typeId) => {
    if (!editingTypes) return;
    const cur = editingTypes.sub_categories || [];
    setEditingTypes({
      ...editingTypes,
      sub_categories: cur.includes(typeId) ? cur.filter((s) => s !== typeId) : [...cur, typeId]
    });
  };

  const saveGameTypes = async () => {
    if (!editingTypes) return;
    try {
      await api.post("/admin/games", {
        id: editingTypes.id,
        name: editingTypes.name,
        image: editingTypes.image,
        sub_categories: editingTypes.sub_categories,
        sort_order: editingTypes.sort_order || 0,
      });
      toast.success("Tipe produk diatur untuk " + editingTypes.name);
      setEditingTypes(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const data = editing || form;
    if (!data.id || !data.name) { toast.error("ID & nama wajib"); return; }
    try {
      // Upsert game first
      await api.post("/admin/games", {
        id: data.id, name: data.name, image: data.image,
        sub_categories: ["akun", "topup_via_id", "topup_via_login", "voucher", "joki"],
        sort_order: parseInt(data.sort_order) || 0,
      });
      // Set fee
      await api.post("/admin/seller-fees", { game: data.id.toLowerCase(), fee_pct: parseFloat(data.fee_pct) || 0 });
      toast.success(editing ? "Kategori diupdate" : "Kategori ditambahkan");
      setForm({ id: "", name: "", image: "", fee_pct: 5, sort_order: 0 });
      setEditing(null);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const updateOrder = async (item, newOrder) => {
    await api.post("/admin/games", {
      id: item.id, name: item.name, image: item.image,
      sub_categories: item.sub_categories || ["akun", "topup_via_id", "topup_via_login", "voucher", "joki"],
      sort_order: parseInt(newOrder) || 0,
    });
    toast.success(`Urutan ${item.name} = ${newOrder}`);
    refresh();
  };

  const moveItem = async (item, direction) => {
    // direction: -1 (up) or +1 (down)
    const idx = items.findIndex((g) => g.id === item.id);
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    const target = items[targetIdx];
    // Swap sort_orders
    await Promise.all([
      updateOrder(item, target.sort_order || targetIdx + 1),
      updateOrder(target, item.sort_order || idx + 1),
    ]);
  };

  const remove = async (item) => {
    if (!window.confirm(`Hapus kategori "${item.name}"? Seller tidak bisa lagi pasang produk di kategori ini.`)) return;
    await api.delete(`/admin/games/${item.id}`);
    await api.delete(`/admin/seller-fees/${item.id.toLowerCase()}`).catch(() => {});
    toast.success("Dihapus");
    refresh();
  };

  const updateFee = async (item, newFee) => {
    await api.post("/admin/seller-fees", { game: item.id.toLowerCase(), fee_pct: parseFloat(newFee) || 0 });
    toast.success(`Fee ${item.name} = ${newFee}%`);
    refresh();
  };

  const target = editing || form;
  return (
    <div data-testid="product-categories-tab">
      <div className="border border-white/10 p-5 mb-6">
        <h3 className="font-display font-bold text-lg text-white mb-1">{editing ? "Edit Kategori Produk" : "Tambah Kategori Produk"}</h3>
        <p className="text-xs text-white/50 mb-4">Seller hanya bisa pasang produk di kategori yang ada di sini. Fee % akan otomatis dipotong saat order terbayar.</p>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-3" data-testid="cat-form">
          <input data-testid="cat-id" required placeholder="kategori-id (mis. mobile-legends)" value={target.id} onChange={(e) => editing ? setEditing({ ...editing, id: e.target.value }) : setForm({ ...form, id: e.target.value })} disabled={!!editing} className="bg-black border border-white/15 p-2 text-sm disabled:opacity-50" />
          <input data-testid="cat-name" required placeholder="Nama Game (mis. Mobile Legends)" value={target.name} onChange={(e) => editing ? setEditing({ ...editing, name: e.target.value }) : setForm({ ...form, name: e.target.value })} className="bg-black border border-white/15 p-2 text-sm" />
          <input data-testid="cat-image" placeholder="URL gambar" value={target.image || ""} onChange={(e) => editing ? setEditing({ ...editing, image: e.target.value }) : setForm({ ...form, image: e.target.value })} className="bg-black border border-white/15 p-2 text-sm" />
          <div className="flex items-center gap-2">
            <input data-testid="cat-fee" type="number" step="0.1" required placeholder="Fee %" value={target.fee_pct ?? ""} onChange={(e) => editing ? setEditing({ ...editing, fee_pct: e.target.value }) : setForm({ ...form, fee_pct: e.target.value })} className="bg-black border border-white/15 p-2 text-sm w-full" />
            <span className="text-white/40 text-xs">% Fee</span>
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="cat-order" type="number" step="1" placeholder="Urutan" value={target.sort_order ?? 0} onChange={(e) => editing ? setEditing({ ...editing, sort_order: e.target.value }) : setForm({ ...form, sort_order: e.target.value })} className="bg-black border border-white/15 p-2 text-sm w-full" />
            <span className="text-white/40 text-xs">Urutan</span>
          </div>
          <div className="md:col-span-2 flex gap-2">
            <button data-testid="cat-save" type="submit" className="btn-primary text-xs"><Plus className="inline w-3 h-3 mr-1" /> {editing ? "Update" : "Tambah"} Kategori</button>
            {editing && <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-white/15 text-white/70 text-xs uppercase tracking-widest">Batal</button>}
          </div>
        </form>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <p className="text-white/50 text-center py-8">Belum ada kategori. Tambahkan di atas — seller tidak bisa pasang produk sampai kamu tambahkan minimal 1.</p>}
        {items.map((g, idx) => (
          <div key={g.id} className="border border-white/10 p-3 flex items-center gap-3" data-testid={`cat-${g.id}`}>
            <div className="flex flex-col gap-0.5">
              <button data-testid={`cat-up-${g.id}`} onClick={() => moveItem(g, -1)} disabled={idx === 0} className="text-white/40 hover:text-[#00F0FF] disabled:opacity-30 disabled:hover:text-white/40 w-6 h-5 flex items-center justify-center border border-white/15">▲</button>
              <button data-testid={`cat-down-${g.id}`} onClick={() => moveItem(g, 1)} disabled={idx === items.length - 1} className="text-white/40 hover:text-[#00F0FF] disabled:opacity-30 disabled:hover:text-white/40 w-6 h-5 flex items-center justify-center border border-white/15">▼</button>
            </div>
            {g.image && <img src={g.image} alt="" className="w-12 h-12 object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-white text-sm">{g.name}</p>
              <p className="text-[11px] text-white/50">{g.id} · urut #{g.sort_order || 0}</p>
            </div>
            <div className="flex items-center gap-1">
              <input
                data-testid={`cat-order-inline-${g.id}`}
                type="number"
                step="1"
                defaultValue={g.sort_order || 0}
                onBlur={(e) => { if (parseInt(e.target.value) !== (g.sort_order || 0)) updateOrder(g, e.target.value); }}
                className="bg-black border border-[#00F0FF]/30 focus:border-[#00F0FF] p-1.5 text-sm w-16 text-center text-[#00F0FF]"
                title="Urutan (kecil = duluan)"
              />
            </div>
            <div className="flex items-center gap-1">
              <input
                data-testid={`cat-fee-inline-${g.id}`}
                type="number"
                step="0.1"
                defaultValue={g.fee_pct}
                onBlur={(e) => { if (parseFloat(e.target.value) !== g.fee_pct) updateFee(g, e.target.value); }}
                className="bg-black border border-[#FFD700]/40 focus:border-[#FFD700] p-1.5 text-sm w-20 text-right text-[#FFD700] font-bold"
              />
              <span className="text-[#FFD700] text-xs">%</span>
            </div>
            <button data-testid={`cat-types-${g.id}`} onClick={() => setEditingTypes({ ...g })} className="text-xs px-3 py-2 border border-[#FFD700]/40 hover:border-[#FFD700] text-[#FFD700]/70 hover:text-[#FFD700] uppercase tracking-widest" title="Atur tipe produk yang diizinkan">Tipe</button>
            <button data-testid={`cat-edit-${g.id}`} onClick={() => setEditing({ ...g })} className="text-xs px-3 py-2 border border-white/15 hover:border-[#00F0FF] text-white/70 hover:text-[#00F0FF] uppercase tracking-widest">Edit</button>
            <button onClick={() => remove(g)} className="text-white/60 hover:text-[#FF003C] p-2"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      {/* ─── Modal Atur Tipe Produk per Game ─── */}
      {editingTypes && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="game-types-modal">
          <div className="w-full max-w-2xl border border-[#FFD700]/40 bg-[#0a0a0a] p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display font-bold text-xl text-white">Atur Tipe Produk</h3>
                <p className="text-sm text-white/60 mt-1">{editingTypes.name}</p>
              </div>
              <button onClick={() => setEditingTypes(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="border border-[#FFD700]/20 bg-[#FFD700]/5 p-4">
              <p className="text-xs text-[#FFD700] mb-3">Pilih tipe produk yang diizinkan untuk game ini. Jika tidak ada yang dipilih, semua tipe aktif akan diizinkan.</p>
              <div className="grid grid-cols-2 gap-2">
                {types.filter(t => t.active).map((t) => {
                  const isSelected = (editingTypes.sub_categories || []).includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`game-type-${t.id}`}
                      onClick={() => toggleGameType(t.id)}
                      className={`px-4 py-3 text-sm font-medium border transition-all ${
                        isSelected
                          ? "border-[#00FF66] bg-[#00FF66]/10 text-[#00FF66]"
                          : "border-white/15 text-white/60 hover:border-white/40"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                          isSelected ? "border-[#00FF66] bg-[#00FF66]" : "border-white/30"
                        }`}>
                          {isSelected && <span className="text-black text-xs">✓</span>}
                        </div>
                        <span>{t.label}</span>
                      </div>
                      {t.description && <p className="text-[10px] text-white/40 mt-1 text-left">{t.description}</p>}
                    </button>
                  );
                })}
              </div>
              {(editingTypes.sub_categories || []).length === 0 && (
                <p className="text-[10px] text-white/50 mt-3 italic">💡 Tidak ada pembatasan - seller bisa pilih semua tipe aktif</p>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={saveGameTypes} className="flex-1 btn-primary text-xs">Simpan Pengaturan</button>
              <button onClick={() => setEditingTypes(null)} className="px-4 py-2 border border-white/15 text-white/70 text-xs uppercase">Batal</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Tipe Produk Section ─── */}
      <div className="mt-10 pt-8 border-t border-white/10">
        <div className="border border-white/10 p-5 mb-5">
          <h3 className="font-display font-bold text-lg text-white mb-1">Tipe Produk</h3>
          <p className="text-xs text-white/50 mb-4">Seller pilih dari list ini saat tambah produk (akun, joki, top-up, voucher, dll). Toggle non-aktif untuk sembunyikan dari seller tanpa hapus.</p>
          <form onSubmit={saveType} className="grid md:grid-cols-4 gap-3" data-testid="type-form">
            <input data-testid="type-id" required placeholder="slug (mis. akun, joki)" value={typeForm.id} onChange={(e) => setTypeForm({ ...typeForm, id: e.target.value })} className="bg-black border border-white/15 p-2 text-sm" />
            <input data-testid="type-label" required placeholder="Label (mis. Akun)" value={typeForm.label} onChange={(e) => setTypeForm({ ...typeForm, label: e.target.value })} className="bg-black border border-white/15 p-2 text-sm" />
            <input data-testid="type-desc" placeholder="Deskripsi singkat" value={typeForm.description} onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })} className="bg-black border border-white/15 p-2 text-sm" />
            <div className="flex items-center gap-2">
              <input data-testid="type-warranty" type="number" min="1" required placeholder="Garansi hari" value={typeForm.warranty_days} onChange={(e) => setTypeForm({ ...typeForm, warranty_days: e.target.value })} className="bg-black border border-white/15 p-2 text-sm w-full" />
              <span className="text-white/40 text-xs whitespace-nowrap">hari garansi</span>
            </div>
            <div className="md:col-span-4 flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
                <input type="checkbox" data-testid="type-active" checked={typeForm.active} onChange={(e) => setTypeForm({ ...typeForm, active: e.target.checked })} className="accent-[#00F0FF]" />
                Aktif
              </label>
              <button data-testid="type-save" type="submit" className="btn-primary text-xs"><Plus className="inline w-3 h-3 mr-1" /> Tambah Tipe</button>
              <span className="text-[10px] text-white/40 ml-auto">Garansi = berapa hari escrow ditahan setelah seller mark "Pesanan Dikirim" sebelum cair otomatis</span>
            </div>
          </form>
        </div>
        <div className="space-y-2">
          {types.length === 0 && <p className="text-white/40 text-xs text-center py-6">Tidak ada tipe produk.</p>}
          {types.map((t) => (
            <div key={t.id} className={`border ${t.active ? "border-white/10" : "border-[#FF003C]/30 bg-[#FF003C]/5"} p-3 flex items-center gap-3`} data-testid={`type-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold text-white text-sm">{t.label}</p>
                  {!t.active && <span className="tag" style={{ borderColor: "rgba(255,0,60,0.6)", color: "#FF003C", background: "rgba(255,0,60,0.1)" }}>NONAKTIF</span>}
                  <span className="tag" style={{ borderColor: "rgba(255,215,0,0.4)", color: "#FFD700", background: "rgba(255,215,0,0.08)" }}>🛡 Garansi {t.warranty_days ?? 3} hari</span>
                  {(t.instant_delivery_fields || []).length > 0 && <span className="tag" style={{ borderColor: "rgba(0,255,102,0.4)", color: "#00FF66", background: "rgba(0,255,102,0.08)" }}>⚡ {(t.instant_delivery_fields || []).length} field</span>}
                </div>
                <p className="text-[11px] text-white/50">{t.id}{t.description ? ` · ${t.description}` : ""}</p>
              </div>
              <input
                data-testid={`type-warranty-edit-${t.id}`}
                type="number" min="1" value={t.warranty_days ?? 3}
                onChange={async (e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!v || v < 1) return;
                  await api.post("/admin/product-types", { ...t, warranty_days: v });
                  refresh();
                }}
                className="w-16 bg-black border border-white/15 p-1 text-xs text-center"
                title="Hari garansi"
              />
              <button data-testid={`type-fields-${t.id}`} onClick={() => setEditingTypeFields({ ...t })} className="text-xs px-3 py-2 border border-[#00FF66]/40 hover:border-[#00FF66] text-[#00FF66]/70 hover:text-[#00FF66] uppercase tracking-widest">⚡ Fields</button>
              <button data-testid={`type-toggle-${t.id}`} onClick={() => toggleType(t)} className={`text-xs px-3 py-2 border uppercase tracking-widest ${t.active ? "border-[#FF003C]/40 text-[#FF003C] hover:bg-[#FF003C]/10" : "border-[#00FF66]/40 text-[#00FF66] hover:bg-[#00FF66]/10"}`}>{t.active ? "Nonaktif" : "Aktif"}</button>
              <button onClick={() => removeType(t)} className="text-white/60 hover:text-[#FF003C] p-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        {/* ─── Modal Edit Instant Delivery Fields ─── */}
        {editingTypeFields && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="type-fields-modal">
            <div className="w-full max-w-2xl border border-[#00FF66]/40 bg-[#0a0a0a] p-6 space-y-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display font-bold text-xl text-white">Instant Delivery Fields</h3>
                  <p className="text-sm text-white/60 mt-1">{editingTypeFields.label}</p>
                  <p className="text-xs text-white/40 mt-2">Tambahkan field deskripsi yang akan ditampilkan saat seller membuat produk tipe ini. Contoh: email, password, login via, dll.</p>
                </div>
                <button onClick={() => setEditingTypeFields(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
              </div>

              <div className="border border-[#00FF66]/20 bg-[#00FF66]/5 p-4 space-y-3">
                {(editingTypeFields.instant_delivery_fields || []).map((field, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-black/40 p-3 border border-white/10">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={field}
                        onChange={(e) => {
                          const updated = [...(editingTypeFields.instant_delivery_fields || [])];
                          updated[idx] = e.target.value;
                          setEditingTypeFields({ ...editingTypeFields, instant_delivery_fields: updated });
                        }}
                        placeholder="Contoh: email, password, login via"
                        className="w-full bg-black border border-white/15 p-2 text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = (editingTypeFields.instant_delivery_fields || []).filter((_, i) => i !== idx);
                        setEditingTypeFields({ ...editingTypeFields, instant_delivery_fields: updated });
                      }}
                      className="text-white/60 hover:text-[#FF003C] p-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    const updated = [...(editingTypeFields.instant_delivery_fields || []), ""];
                    setEditingTypeFields({ ...editingTypeFields, instant_delivery_fields: updated });
                  }}
                  className="w-full px-4 py-2 border border-[#00FF66]/40 hover:border-[#00FF66] text-[#00FF66]/70 hover:text-[#00FF66] text-xs uppercase tracking-widest font-bold"
                >
                  + Tambah Field
                </button>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={async () => {
                    try {
                      await api.post("/admin/product-types", {
                        ...editingTypeFields,
                        instant_delivery_fields: (editingTypeFields.instant_delivery_fields || []).filter(f => f.trim()),
                      });
                      toast.success("Fields tersimpan");
                      setEditingTypeFields(null);
                      refresh();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Gagal");
                    }
                  }}
                  className="flex-1 btn-primary text-xs"
                >
                  Simpan Fields
                </button>
                <button onClick={() => setEditingTypeFields(null)} className="px-4 py-2 border border-white/15 text-white/70 text-xs uppercase">Batal</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductRequestsTab() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [responseInput, setResponseInput] = useState({});

  const refresh = () => api.get("/admin/product-requests").then((r) => setRequests(r.data));
  useEffect(() => { refresh(); }, []);

  const respond = async (req, status) => {
    const resp = responseInput[req.id] || "";
    await api.post(`/admin/product-requests/${req.id}/respond`, { status, response: resp });
    toast.success(`Request ${status === "resolved" ? "disetujui" : "ditolak"}`);
    setResponseInput({ ...responseInput, [req.id]: "" });
    refresh();
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus request ini?")) return;
    await api.delete(`/admin/product-requests/${id}`);
    refresh();
  };

  const filtered = filter === "all" ? requests : requests.filter((r) => r.status === filter);

  return (
    <div data-testid="product-requests-tab">
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "pending", label: "Pending", color: "#FFD700" },
          { id: "resolved", label: "Disetujui", color: "#00FF66" },
          { id: "rejected", label: "Ditolak", color: "#FF003C" },
          { id: "all", label: "Semua", color: "#94A3B8" },
        ].map((f) => {
          const count = f.id === "all" ? requests.length : requests.filter((r) => r.status === f.id).length;
          return (
            <button key={f.id} data-testid={`req-filter-${f.id}`} onClick={() => setFilter(f.id)} className={`px-4 py-2 text-xs uppercase tracking-widest border ${filter === f.id ? "bg-white/5" : ""}`} style={{ borderColor: filter === f.id ? f.color : "rgba(255,255,255,0.15)", color: filter === f.id ? f.color : "rgba(255,255,255,0.7)" }}>
              {f.label} ({count})
            </button>
          );
        })}
      </div>

      {filtered.length === 0 && <p className="text-white/50 text-center py-12">Tidak ada request {filter !== "all" && filter}.</p>}

      <div className="space-y-3">
        {filtered.map((r) => (
          <div key={r.id} className="border border-white/10 p-5" data-testid={`req-item-${r.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-bold text-white text-base">{r.title}</h4>
                  <span className="tag" style={r.status === "pending" ? { borderColor: "rgba(255,215,0,0.6)", color: "#FFD700", background: "rgba(255,215,0,0.1)" } : r.status === "resolved" ? { borderColor: "rgba(0,255,102,0.6)", color: "#00FF66", background: "rgba(0,255,102,0.1)" } : { borderColor: "rgba(255,0,60,0.6)", color: "#FF003C", background: "rgba(255,0,60,0.1)" }}>{r.status}</span>
                </div>
                <p className="text-xs text-white/50 mt-1">{r.seller_name} · {r.seller_email} · {new Date(r.created_at).toLocaleString("id-ID")}</p>
              </div>
              <button onClick={() => remove(r.id)} className="text-white/40 hover:text-[#FF003C] p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
            <p className="text-white/80 text-sm whitespace-pre-wrap mb-3">{r.message}</p>
            {r.admin_response && (
              <div className="bg-[#00F0FF]/5 border border-[#00F0FF]/20 p-3 mb-3">
                <p className="text-[10px] uppercase tracking-widest text-[#00F0FF] mb-1">Respon Admin ({r.responded_by})</p>
                <p className="text-white/80 text-sm whitespace-pre-wrap">{r.admin_response}</p>
              </div>
            )}
            {r.status === "pending" && (
              <div className="space-y-2 pt-3 border-t border-white/10">
                <textarea
                  data-testid={`req-response-${r.id}`}
                  rows={2}
                  placeholder="Balasan admin (opsional, mis. 'Sudah ditambahkan ke kategori, silakan pasang')"
                  value={responseInput[r.id] || ""}
                  onChange={(e) => setResponseInput({ ...responseInput, [r.id]: e.target.value })}
                  className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-2 text-sm"
                />
                <div className="flex gap-2">
                  <button data-testid={`req-approve-${r.id}`} onClick={() => respond(r, "resolved")} className="px-4 py-2 bg-[#00FF66]/20 hover:bg-[#00FF66]/30 border border-[#00FF66]/40 text-[#00FF66] text-xs uppercase tracking-widest font-bold">Setujui</button>
                  <button data-testid={`req-reject-${r.id}`} onClick={() => respond(r, "rejected")} className="px-4 py-2 bg-[#FF003C]/20 hover:bg-[#FF003C]/30 border border-[#FF003C]/40 text-[#FF003C] text-xs uppercase tracking-widest font-bold">Tolak</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductDetailTab() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | out_of_stock
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null); // product to delete
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("q", search.trim());
      if (statusFilter !== "all") params.set("status", statusFilter);
      const { data } = await api.get(`/admin/products?${params}`);
      setProducts(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (e) => { e.preventDefault(); refresh(); };

  const confirmDelete = async () => {
    if (!reason.trim() || reason.trim().length < 5) { toast.error("Alasan wajib (min 5 karakter)"); return; }
    setBusy(true);
    try {
      await api.delete(`/admin/products/${deleteModal.id}`, { data: { reason: reason.trim() } });
      toast.success(`Produk "${deleteModal.title}" dihapus. Seller akan dinotifikasi.`);
      setDeleteModal(null);
      setReason("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal hapus");
    } finally { setBusy(false); }
  };

  const stats = {
    total: products.length,
    active: products.filter((p) => (p.stock || 0) > 0).length,
    out: products.filter((p) => (p.stock || 0) <= 0).length,
  };

  return (
    <div data-testid="product-detail-tab">
      <form onSubmit={onSearch} className="flex flex-wrap gap-2 mb-4">
        <div className="flex-1 min-w-[260px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="pd-search"
            type="text"
            placeholder="Cari nama produk, nama toko, atau email seller..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-black border border-white/15 focus:border-[#00F0FF] pl-10 pr-3 py-2 text-sm"
          />
          {search && (
            <button type="button" onClick={() => { setSearch(""); setTimeout(refresh, 0); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button data-testid="pd-search-btn" type="submit" className="btn-primary text-xs">Cari</button>
      </form>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "all", label: `Semua (${stats.total})`, color: "#94A3B8" },
          { id: "active", label: `Aktif (${stats.active})`, color: "#00FF66" },
          { id: "out_of_stock", label: `Habis Stock (${stats.out})`, color: "#FF6B6B" },
        ].map((f) => (
          <button
            key={f.id}
            data-testid={`pd-filter-${f.id}`}
            onClick={() => setStatusFilter(f.id)}
            className={`px-4 py-2 text-xs uppercase tracking-widest border ${statusFilter === f.id ? "bg-white/5" : ""}`}
            style={{ borderColor: statusFilter === f.id ? f.color : "rgba(255,255,255,0.15)", color: statusFilter === f.id ? f.color : "rgba(255,255,255,0.7)" }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-white/50 text-center py-8">Memuat...</p>}

      {!loading && products.length === 0 && (
        <p className="text-white/50 text-center py-12">Tidak ada produk{search ? ` cocok "${search}"` : ""}.</p>
      )}

      <div className="space-y-2">
        {products.map((p) => {
          const isOut = (p.stock || 0) <= 0;
          return (
            <div key={p.id} className={`border ${isOut ? "border-[#FF6B6B]/30 bg-[#FF6B6B]/5" : "border-white/10"} p-3 flex items-center gap-3`} data-testid={`pd-row-${p.id}`}>
              {p.image_url && <img src={p.image_url} alt="" className="w-14 h-14 object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold text-white text-sm truncate max-w-md">{p.title}</p>
                  {isOut ? (
                    <span className="tag" style={{ borderColor: "rgba(255,107,107,0.6)", color: "#FF6B6B", background: "rgba(255,107,107,0.1)" }}>STOCK HABIS</span>
                  ) : (
                    <span className="tag tag-accent">AKTIF · stock {p.stock}</span>
                  )}
                </div>
                <p className="text-[11px] text-white/50 mt-0.5">
                  <span className="text-white/70">{p.shop_name || "—"}</span>
                  {" · "}{p.seller_email}
                  {" · "}{p.category}
                  {" · "}<span className="text-[#FFD700] font-bold">{formatIDR(p.price)}</span>
                  {" · "}sold {p.sold || 0}
                </p>
              </div>
              <button
                data-testid={`pd-delete-${p.id}`}
                onClick={() => { setDeleteModal(p); setReason(""); }}
                className="inline-flex items-center gap-1 text-xs px-3 py-2 border border-[#FF003C]/40 text-[#FF003C] hover:bg-[#FF003C]/10 uppercase tracking-widest"
              >
                <Trash2 className="w-3.5 h-3.5" /> Hapus
              </button>
            </div>
          );
        })}
      </div>

      {deleteModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => !busy && setDeleteModal(null)}>
          <div className="bg-[#050505] border border-[#FF003C]/40 max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="pd-delete-modal">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="font-display font-black text-xl text-white">Hapus Produk</h3>
                <p className="text-xs text-white/60 mt-1 truncate">{deleteModal.title} · {deleteModal.shop_name}</p>
              </div>
              <button onClick={() => !busy && setDeleteModal(null)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="mb-4 p-3 bg-[#FF003C]/10 border border-[#FF003C]/30 text-xs text-[#FF6B6B]">
              ⚠ Aksi ini PERMANEN. Seller akan otomatis dinotifikasi dengan alasan yang kamu tulis di bawah.
            </div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60">Alasan Penghapusan (wajib, min 5 karakter)</label>
            <textarea
              data-testid="pd-reason"
              rows={4}
              autoFocus
              placeholder="Mis. Melanggar T&C — menjual akun hasil hack..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-black border border-white/15 focus:border-[#FF003C] p-3 text-sm mt-2"
            />
            <div className="flex gap-2 mt-4">
              <button data-testid="pd-confirm-delete" disabled={busy || reason.trim().length < 5} onClick={confirmDelete} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#FF003C] hover:bg-[#cc002f] text-white text-xs uppercase tracking-widest font-bold disabled:opacity-40">
                <Trash2 className="w-4 h-4" /> {busy ? "Menghapus..." : "Hapus Produk"}
              </button>
              <button disabled={busy} onClick={() => setDeleteModal(null)} className="px-4 py-3 border border-white/15 text-white/70 text-xs uppercase tracking-widest">Batal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function AuctionDetailTab() {
  const [auctions, setAuctions] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all|live|frozen|sold|ended|cancelled
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("q", search.trim());
      if (statusFilter !== "all") params.set("status", statusFilter);
      const { data } = await api.get(`/admin/auctions?${params}`);
      setAuctions(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (e) => { e.preventDefault(); refresh(); };

  const confirmDelete = async () => {
    if (!reason.trim() || reason.trim().length < 5) { toast.error("Alasan wajib (min 5 karakter)"); return; }
    setBusy(true);
    try {
      await api.delete(`/admin/auctions/${deleteModal.id}`, { data: { reason: reason.trim() } });
      toast.success(`Auction "${deleteModal.title}" dihapus. Seller dinotifikasi & bid aktif di-refund.`);
      setDeleteModal(null); setReason(""); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal hapus"); }
    finally { setBusy(false); }
  };

  const stats = {
    total: auctions.length,
    live: auctions.filter((a) => a.status === "live").length,
    sold: auctions.filter((a) => a.status === "sold").length,
    ended: auctions.filter((a) => ["ended", "cancelled"].includes(a.status)).length,
  };

  const statusColor = (s) => ({
    live: "#00FF66", frozen: "#FFD700", sold: "#00F0FF", ended: "#94A3B8", cancelled: "#FF6B6B",
  }[s] || "#94A3B8");

  return (
    <div data-testid="auction-detail-tab">
      <form onSubmit={onSearch} className="flex flex-wrap gap-2 mb-4">
        <div className="flex-1 min-w-[260px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="ad-search"
            type="text"
            placeholder="Cari nama auction, game, nama toko, atau email seller..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-black border border-white/15 focus:border-[#00F0FF] pl-10 pr-3 py-2 text-sm"
          />
          {search && (
            <button type="button" onClick={() => { setSearch(""); setTimeout(refresh, 0); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button data-testid="ad-search-btn" type="submit" className="btn-primary text-xs">Cari</button>
      </form>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "all", label: `Semua (${stats.total})`, color: "#94A3B8" },
          { id: "live", label: `Live (${stats.live})`, color: "#00FF66" },
          { id: "sold", label: `Sold (${stats.sold})`, color: "#00F0FF" },
          { id: "ended", label: `Berakhir (${stats.ended})`, color: "#FF6B6B" },
        ].map((f) => (
          <button
            key={f.id}
            data-testid={`ad-filter-${f.id}`}
            onClick={() => setStatusFilter(f.id)}
            className={`px-4 py-2 text-xs uppercase tracking-widest border ${statusFilter === f.id ? "bg-white/5" : ""}`}
            style={{ borderColor: statusFilter === f.id ? f.color : "rgba(255,255,255,0.15)", color: statusFilter === f.id ? f.color : "rgba(255,255,255,0.7)" }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-white/50 text-center py-8">Memuat...</p>}
      {!loading && auctions.length === 0 && <p className="text-white/50 text-center py-12">Tidak ada auction{search ? ` cocok "${search}"` : ""}.</p>}

      <div className="space-y-2">
        {auctions.map((a) => (
          <div key={a.id} className="border border-white/10 p-3 flex items-center gap-3" data-testid={`ad-row-${a.id}`}>
            {a.image_url && <img src={a.image_url} alt="" className="w-14 h-14 object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-white text-sm truncate max-w-md">{a.title}</p>
                <span className="tag" style={{ borderColor: `${statusColor(a.status)}80`, color: statusColor(a.status), background: `${statusColor(a.status)}1A` }}>{(a.status || "").toUpperCase()}</span>
                {a.buy_now_price && <span className="tag" style={{ borderColor: "rgba(255,215,0,0.6)", color: "#FFD700", background: "rgba(255,215,0,0.1)" }}>BIN {formatIDR(a.buy_now_price)}</span>}
              </div>
              <p className="text-[11px] text-white/50 mt-0.5">
                <span className="text-white/70">{a.shop_name || "—"}</span>
                {" · "}{a.seller_email}
                {" · "}{a.game_name} / {a.sub_category}
                {" · "}<span className="text-[#FFD700] font-bold">{formatIDR(a.current_bid_amount || a.starting_bid)}</span>
                {" · "}bids {a.bid_count || 0}
              </p>
            </div>
            <button
              data-testid={`ad-delete-${a.id}`}
              onClick={() => { setDeleteModal(a); setReason(""); }}
              className="inline-flex items-center gap-1 text-xs px-3 py-2 border border-[#FF003C]/40 text-[#FF003C] hover:bg-[#FF003C]/10 uppercase tracking-widest"
            >
              <Trash2 className="w-3.5 h-3.5" /> Hapus
            </button>
          </div>
        ))}
      </div>

      {deleteModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => !busy && setDeleteModal(null)}>
          <div className="bg-[#050505] border border-[#FF003C]/40 max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="ad-delete-modal">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="font-display font-black text-xl text-white">Hapus Auction</h3>
                <p className="text-xs text-white/60 mt-1 truncate">{deleteModal.title} · {deleteModal.shop_name}</p>
              </div>
              <button onClick={() => !busy && setDeleteModal(null)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="mb-4 p-3 bg-[#FF003C]/10 border border-[#FF003C]/30 text-xs text-[#FF6B6B] space-y-1">
              <p>⚠ Aksi ini PERMANEN.</p>
              {deleteModal.current_bid_user_id && deleteModal.status === "live" && (
                <p>💰 Saldo bidder aktif sebesar <b>{formatIDR(deleteModal.current_bid_amount)}</b> akan otomatis di-refund.</p>
              )}
              <p>📨 Seller akan dinotifikasi dengan alasan di bawah.</p>
            </div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60">Alasan Penghapusan (wajib, min 5 karakter)</label>
            <textarea
              data-testid="ad-reason"
              rows={4}
              autoFocus
              placeholder="Mis. Item dari hasil hack — melanggar T&C..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-black border border-white/15 focus:border-[#FF003C] p-3 text-sm mt-2"
            />
            <div className="flex gap-2 mt-4">
              <button data-testid="ad-confirm-delete" disabled={busy || reason.trim().length < 5} onClick={confirmDelete} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#FF003C] hover:bg-[#cc002f] text-white text-xs uppercase tracking-widest font-bold disabled:opacity-40">
                <Trash2 className="w-4 h-4" /> {busy ? "Menghapus..." : "Hapus Auction"}
              </button>
              <button disabled={busy} onClick={() => setDeleteModal(null)} className="px-4 py-3 border border-white/15 text-white/70 text-xs uppercase tracking-widest">Batal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function PaymentMethodsTab() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all|active|inactive
  const [editing, setEditing] = useState(null); // method being edited
  const [addForm, setAddForm] = useState({ code: "", label: "", active: true, sort_order: 0 });
  const [showAdd, setShowAdd] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payment-methods");
      setMethods(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveOverride = async (m, patch) => {
    const payload = {
      code: m.code,
      label: patch.label !== undefined ? patch.label : (m.admin_label !== m.name ? m.admin_label : null),
      active: patch.active !== undefined ? patch.active : m.admin_active,
      sort_order: patch.sort_order !== undefined ? parseInt(patch.sort_order) : (m.sort_order || 0),
    };
    try {
      await api.post("/admin/payment-methods", payload);
      toast.success(`${m.code} disimpan`);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.code.trim()) { toast.error("Kode wajib"); return; }
    try {
      await api.post("/admin/payment-methods", { ...addForm, code: addForm.code.trim().toUpperCase() });
      toast.success("Pembayaran ditambahkan");
      setAddForm({ code: "", label: "", active: true, sort_order: 0 });
      setShowAdd(false);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    await saveOverride(editing, { label: editing.admin_label, active: editing.admin_active, sort_order: editing.sort_order });
    setEditing(null);
  };

  const removeOverride = async (m) => {
    if (!window.confirm(`Hapus override untuk "${m.code}"? Akan kembali ke pengaturan default Tripay.`)) return;
    try {
      await api.delete(`/admin/payment-methods/${m.code}`);
      toast.success("Override dihapus");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const filtered = methods.filter((m) => {
    if (filter === "active") return m.admin_active && m.active;
    if (filter === "inactive") return !m.admin_active || !m.active;
    return true;
  });

  const stats = {
    total: methods.length,
    active: methods.filter((m) => m.admin_active && m.active).length,
    inactive: methods.filter((m) => !m.admin_active || !m.active).length,
  };

  return (
    <div data-testid="payment-methods-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex gap-2 flex-wrap">
          {[
            { id: "all", label: `Semua (${stats.total})`, color: "#94A3B8" },
            { id: "active", label: `Aktif (${stats.active})`, color: "#00FF66" },
            { id: "inactive", label: `Non-aktif (${stats.inactive})`, color: "#FF6B6B" },
          ].map((f) => (
            <button key={f.id} data-testid={`pm-filter-${f.id}`} onClick={() => setFilter(f.id)} className={`px-4 py-2 text-xs uppercase tracking-widest border ${filter === f.id ? "bg-white/5" : ""}`} style={{ borderColor: filter === f.id ? f.color : "rgba(255,255,255,0.15)", color: filter === f.id ? f.color : "rgba(255,255,255,0.7)" }}>
              {f.label}
            </button>
          ))}
        </div>
        <button data-testid="pm-add-btn" onClick={() => setShowAdd((v) => !v)} className="btn-primary text-xs inline-flex items-center gap-2">
          <Plus className="w-3 h-3" /> {showAdd ? "Tutup" : "Tambah Pembayaran"}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={submitAdd} className="border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-4 mb-4 grid md:grid-cols-4 gap-3" data-testid="pm-add-form">
          <input data-testid="pm-add-code" required placeholder="Kode (BCAVA, OVO, QRIS...)" value={addForm.code} onChange={(e) => setAddForm({ ...addForm, code: e.target.value.toUpperCase() })} className="bg-black border border-white/15 p-2 text-sm" />
          <input data-testid="pm-add-label" placeholder="Label tampilan (opsional)" value={addForm.label} onChange={(e) => setAddForm({ ...addForm, label: e.target.value })} className="bg-black border border-white/15 p-2 text-sm" />
          <input data-testid="pm-add-order" type="number" placeholder="Urutan (0)" value={addForm.sort_order} onChange={(e) => setAddForm({ ...addForm, sort_order: parseInt(e.target.value) || 0 })} className="bg-black border border-white/15 p-2 text-sm" />
          <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
            <input data-testid="pm-add-active" type="checkbox" checked={addForm.active} onChange={(e) => setAddForm({ ...addForm, active: e.target.checked })} className="accent-[#00F0FF]" />
            Aktif
          </label>
          <p className="md:col-span-4 text-[11px] text-white/50">Tip: kode harus sesuai daftar resmi Tripay (mis. BCAVA, BNIVA, BRIVA, OVO, QRIS, SHOPEEPAY). Override label & status saja, fee tetap dari Tripay.</p>
          <div className="md:col-span-4 flex gap-2">
            <button data-testid="pm-add-submit" type="submit" className="btn-primary text-xs">Simpan</button>
            <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 border border-white/15 text-white/70 text-xs uppercase tracking-widest">Batal</button>
          </div>
        </form>
      )}

      {loading && <p className="text-white/50 text-center py-8">Memuat dari Tripay...</p>}
      {!loading && filtered.length === 0 && <p className="text-white/50 text-center py-12">Tidak ada pembayaran.</p>}

      <div className="space-y-2">
        {filtered.map((m) => {
          const customized = m.admin_label !== m.name || !m.admin_active || (m.sort_order || 0) > 0;
          const trulyActive = m.admin_active && m.active;
          return (
            <div key={m.code} className={`border ${trulyActive ? "border-white/10" : "border-[#FF6B6B]/30 bg-[#FF6B6B]/5"} p-3 flex items-center gap-3`} data-testid={`pm-row-${m.code}`}>
              {m.icon_url && <img src={m.icon_url} alt="" className="w-12 h-12 object-contain bg-white p-1" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold text-white text-sm">{m.admin_label || m.name}</p>
                  <span className="tag" style={{ borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.6)" }}>{m.code}</span>
                  {!m.admin_active && <span className="tag" style={{ borderColor: "rgba(255,107,107,0.6)", color: "#FF6B6B", background: "rgba(255,107,107,0.1)" }}>OFF BY ADMIN</span>}
                  {!m.active && <span className="tag" style={{ borderColor: "rgba(255,215,0,0.6)", color: "#FFD700", background: "rgba(255,215,0,0.1)" }}>OFF DI TRIPAY</span>}
                  {customized && <span className="tag tag-accent">CUSTOM</span>}
                </div>
                <p className="text-[11px] text-white/50 mt-0.5">
                  {m.group}{m.fee_customer?.flat ? ` · Fee: Rp ${m.fee_customer.flat.toLocaleString("id-ID")}` : ""}{m.fee_customer?.percent ? ` (${m.fee_customer.percent}%)` : ""}{(m.sort_order || 0) > 0 ? ` · urut ${m.sort_order}` : ""}
                </p>
              </div>
              <button
                data-testid={`pm-toggle-${m.code}`}
                onClick={() => saveOverride(m, { active: !m.admin_active })}
                className={`text-xs px-3 py-2 border uppercase tracking-widest ${m.admin_active ? "border-[#FF003C]/40 text-[#FF003C] hover:bg-[#FF003C]/10" : "border-[#00FF66]/40 text-[#00FF66] hover:bg-[#00FF66]/10"}`}
              >
                {m.admin_active ? "Nonaktif" : "Aktifkan"}
              </button>
              <button data-testid={`pm-edit-${m.code}`} onClick={() => setEditing({ ...m })} className="text-xs px-3 py-2 border border-white/15 hover:border-[#00F0FF] text-white/70 hover:text-[#00F0FF] uppercase tracking-widest">Edit</button>
              {customized && (
                <button data-testid={`pm-reset-${m.code}`} onClick={() => removeOverride(m)} className="text-white/60 hover:text-[#FF003C] p-2" title="Reset ke default Tripay">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onSubmit={submitEdit} className="bg-[#050505] border border-white/15 max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="pm-edit-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display font-black text-xl text-white">Edit Pembayaran</h3>
                <p className="text-xs text-white/60 mt-1">{editing.code} · {editing.group}</p>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">Nama Tampilan (label)</label>
                <input data-testid="pm-edit-label" value={editing.admin_label || ""} onChange={(e) => setEditing({ ...editing, admin_label: e.target.value })} placeholder={editing.name} className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2" />
                <p className="text-[10px] text-white/40 mt-1">Default Tripay: {editing.name}. Kosongkan untuk pakai default.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">Urutan Tampil</label>
                <input data-testid="pm-edit-order" type="number" value={editing.sort_order || 0} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2" />
                <p className="text-[10px] text-white/40 mt-1">Angka kecil = tampil duluan. 0 = urutan default.</p>
              </div>
              <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer pt-1">
                <input data-testid="pm-edit-active" type="checkbox" checked={!!editing.admin_active} onChange={(e) => setEditing({ ...editing, admin_active: e.target.checked })} className="accent-[#00F0FF] w-4 h-4" />
                Aktifkan untuk publik
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <button data-testid="pm-edit-submit" type="submit" className="btn-primary text-xs flex-1">Simpan</button>
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-white/15 text-white/70 text-xs uppercase tracking-widest">Batal</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


function TopupHistoryTab() {
  const [topups, setTopups] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("q", search.trim());
      if (statusFilter !== "all") params.set("status", statusFilter);
      const [list, s] = await Promise.all([
        api.get(`/admin/topups?${params}`).then((r) => r.data),
        api.get("/admin/topups/stats").then((r) => r.data),
      ]);
      setTopups(list);
      setStats(s);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [statusFilter]);

  const onSearch = (e) => { e.preventDefault(); refresh(); };

  const statusColor = (s) => ({
    paid: "#00FF66", pending: "#FFD700", failed: "#FF003C", expired: "#FF6B6B", refund: "#94A3B8",
  }[s] || "#94A3B8");

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  return (
    <div data-testid="topup-history-tab">
      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="border border-white/10 p-4">
            <p className="text-[10px] uppercase tracking-widest text-white/40">Total Topup</p>
            <p className="font-display font-black text-2xl text-white mt-1">{stats.total}</p>
          </div>
          <div className="border border-[#00FF66]/30 bg-[#00FF66]/5 p-4">
            <p className="text-[10px] uppercase tracking-widest text-[#00FF66]/80">Berhasil</p>
            <p className="font-display font-black text-2xl text-[#00FF66] mt-1">{stats.paid_count}</p>
            <p className="text-[10px] text-white/60 mt-1">{formatIDR(stats.total_paid_amount)}</p>
          </div>
          <div className="border border-[#FFD700]/30 bg-[#FFD700]/5 p-4">
            <p className="text-[10px] uppercase tracking-widest text-[#FFD700]/80">Pending</p>
            <p className="font-display font-black text-2xl text-[#FFD700] mt-1">{stats.pending_count}</p>
          </div>
          <div className="border border-[#FF003C]/30 bg-[#FF003C]/5 p-4">
            <p className="text-[10px] uppercase tracking-widest text-[#FF003C]/80">Gagal/Expired</p>
            <p className="font-display font-black text-2xl text-[#FF003C] mt-1">{stats.failed_count}</p>
          </div>
        </div>
      )}

      <form onSubmit={onSearch} className="flex flex-wrap gap-2 mb-4">
        <div className="flex-1 min-w-[260px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="tp-search"
            type="text"
            placeholder="Cari merchant_ref, tripay_ref, email, atau nama user..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-black border border-white/15 focus:border-[#00F0FF] pl-10 pr-3 py-2 text-sm"
          />
          {search && (
            <button type="button" onClick={() => { setSearch(""); setTimeout(refresh, 0); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button data-testid="tp-search-btn" type="submit" className="btn-primary text-xs">Cari</button>
      </form>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "all", label: "Semua", color: "#94A3B8" },
          { id: "paid", label: "Berhasil", color: "#00FF66" },
          { id: "pending", label: "Pending", color: "#FFD700" },
          { id: "failed", label: "Gagal", color: "#FF003C" },
          { id: "expired", label: "Expired", color: "#FF6B6B" },
        ].map((f) => (
          <button key={f.id} data-testid={`tp-filter-${f.id}`} onClick={() => setStatusFilter(f.id)} className={`px-4 py-2 text-xs uppercase tracking-widest border ${statusFilter === f.id ? "bg-white/5" : ""}`} style={{ borderColor: statusFilter === f.id ? f.color : "rgba(255,255,255,0.15)", color: statusFilter === f.id ? f.color : "rgba(255,255,255,0.7)" }}>
            {f.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-white/50 text-center py-8">Memuat...</p>}
      {!loading && topups.length === 0 && <p className="text-white/50 text-center py-12">Belum ada topup{search ? ` cocok "${search}"` : ""}.</p>}

      <div className="space-y-2">
        {topups.map((t) => (
          <div key={t.id} className="border border-white/10 hover:border-white/30 p-3 flex items-center gap-3 cursor-pointer transition-colors" data-testid={`tp-row-${t.id}`} onClick={() => setDetail(t)}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-white text-sm">{t.merchant_ref}</span>
                <span className="tag" style={{ borderColor: `${statusColor(t.status)}80`, color: statusColor(t.status), background: `${statusColor(t.status)}1A` }}>{(t.status || "").toUpperCase()}</span>
                <span className="tag" style={{ borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.6)" }}>{t.payment_method}</span>
              </div>
              <p className="text-[11px] text-white/50 mt-0.5">
                <span className="text-white/70">{t.user_name || "—"}</span>
                {" · "}{t.user_email}
                {" · "}<span className="text-white/40">{t.user_role}</span>
                {" · "}<span className="text-white/60">{fmtDate(t.created_at)}</span>
              </p>
            </div>
            <div className="text-right">
              <p className="font-display font-black text-lg text-[#00F0FF]">{formatIDR(t.amount)}</p>
              {t.tripay_reference && <p className="text-[10px] text-white/40 font-mono">{t.tripay_reference}</p>}
            </div>
          </div>
        ))}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-[#050505] border border-white/15 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="tp-detail-modal">
            <div className="flex items-start justify-between gap-3 mb-5 pb-4 border-b border-white/10">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">// Detail Top-up</p>
                <h3 className="font-display font-black text-2xl text-white">{formatIDR(detail.amount)}</h3>
                <span className="tag mt-2 inline-block" style={{ borderColor: `${statusColor(detail.status)}80`, color: statusColor(detail.status), background: `${statusColor(detail.status)}1A` }}>{(detail.status || "").toUpperCase()}</span>
              </div>
              <button onClick={() => setDetail(null)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Detail label="User">{detail.user_name || "—"}</Detail>
              <Detail label="Email">{detail.user_email}</Detail>
              <Detail label="Role" mono>{detail.user_role}</Detail>
              <Detail label="Channel" mono>{detail.payment_method}</Detail>
              <Detail label="Merchant Ref" mono>{detail.merchant_ref}</Detail>
              <Detail label="Tripay Reference" mono>{detail.tripay_reference || "—"}</Detail>
              <Detail label="Dibuat (WIB)">{fmtDate(detail.created_at)}</Detail>
              <Detail label="Settled (WIB)">{fmtDate(detail.settled_at || detail.paid_at)}</Detail>
              <Detail label="User ID" mono full>{detail.user_id}</Detail>
              <Detail label="Topup ID" mono full>{detail.id}</Detail>
            </div>

            {detail.checkout_url && (
              <a href={detail.checkout_url} target="_blank" rel="noopener noreferrer" className="block mt-5 text-center px-4 py-3 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 text-xs uppercase tracking-widest font-bold" data-testid="tp-checkout-link">
                Buka Checkout URL Tripay ↗
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, children, mono, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
      <p className={`text-white/90 mt-1 break-all ${mono ? "font-mono" : ""}`}>{children}</p>
    </div>
  );
}


function VouchersTab() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    code: "", discount_type: "percentage", value: 5,
    min_order_amount: 0, max_uses: "", expires_at: "",
    per_user_limit: "unlimited",  // "unlimited" | "once"
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/vouchers");
      setVouchers(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error("Kode wajib"); return; }
    if (!form.value || form.value <= 0) { toast.error("Nilai diskon wajib"); return; }
    if (form.discount_type === "percentage" && (form.value < 1 || form.value > 100)) {
      toast.error("Persen harus 1-100"); return;
    }
    const payload = {
      code: form.code.trim().toUpperCase(),
      discount_type: form.discount_type,
      value: parseFloat(form.value),
      min_order_amount: parseFloat(form.min_order_amount) || 0,
      max_uses: form.max_uses ? parseInt(form.max_uses) : null,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      per_user_limit: form.per_user_limit === "once" ? 1 : null,
    };
    try {
      await api.post("/admin/vouchers", payload);
      toast.success(`Voucher ${payload.code} ditambahkan`);
      setForm({ code: "", discount_type: "percentage", value: 5, min_order_amount: 0, max_uses: "", expires_at: "", per_user_limit: "unlimited" });
      setShowAdd(false);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const toggleActive = async (v) => {
    await api.patch(`/admin/vouchers/${v.id}`, { active: !v.active });
    toast.success(v.active ? "Voucher di-nonaktif" : "Voucher diaktifkan");
    refresh();
  };

  const remove = async (v) => {
    if (!window.confirm(`Hapus voucher "${v.code}"? Aksi permanen.`)) return;
    await api.delete(`/admin/vouchers/${v.id}`);
    toast.success("Dihapus");
    refresh();
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const isExpired = (v) => v.expires_at && new Date(v.expires_at) < new Date();

  return (
    <div data-testid="vouchers-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-white/40">// VOUCHER GLOBAL</p>
          <h3 className="font-display font-bold text-xl text-white mt-1">Voucher diskon untuk semua produk seller</h3>
          <p className="text-[11px] text-white/50 mt-1">Berlaku di seluruh marketplace — buyer bisa input kode saat checkout untuk dapat diskon.</p>
        </div>
        <button data-testid="vc-add-btn" onClick={() => setShowAdd((v) => !v)} className="btn-primary text-xs inline-flex items-center gap-2">
          <Plus className="w-3 h-3" /> {showAdd ? "Tutup" : "Tambah Voucher"}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={submit} className="border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-5 mb-5 grid md:grid-cols-2 gap-3" data-testid="vc-form">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/60">Kode Voucher</label>
            <input data-testid="vc-code" required placeholder="USTORE5, PROMO10K" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="w-full bg-black border border-white/15 p-2 text-sm mt-1 font-mono" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/60">Tipe Diskon</label>
            <select data-testid="vc-type" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm mt-1">
              <option value="percentage">Persentase (%)</option>
              <option value="fixed">Nominal (Rp)</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/60">{form.discount_type === "percentage" ? "Persen (1-100)" : "Nominal (Rp)"}</label>
            <input data-testid="vc-value" required type="number" step={form.discount_type === "percentage" ? "1" : "1000"} min="1" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/60">Min Belanja (Rp, opsional)</label>
            <input data-testid="vc-min" type="number" step="1000" min="0" value={form.min_order_amount} onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/60">Max Pemakaian (opsional, kosong = unlimited)</label>
            <input data-testid="vc-max-uses" type="number" min="1" placeholder="100" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/60">Kadaluarsa (opsional)</label>
            <input data-testid="vc-expires" type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="w-full bg-black border border-white/15 p-2 text-sm mt-1" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-white/60 mb-2 block">Pemakaian per Akun</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="vc-limit-unlimited" onClick={() => setForm({ ...form, per_user_limit: "unlimited" })} className={`p-3 border text-sm font-bold uppercase tracking-widest ${form.per_user_limit === "unlimited" ? "border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-white/60 hover:border-white/30"}`}>
                Bebas (unlimited)
              </button>
              <button type="button" data-testid="vc-limit-once" onClick={() => setForm({ ...form, per_user_limit: "once" })} className={`p-3 border text-sm font-bold uppercase tracking-widest ${form.per_user_limit === "once" ? "border-[#FFD700] bg-[#FFD700]/10 text-[#FFD700]" : "border-white/15 text-white/60 hover:border-white/30"}`}>
                1× per akun
              </button>
            </div>
            <p className="text-[10px] text-white/40 mt-1">
              {form.per_user_limit === "once"
                ? "Tiap user hanya bisa pakai sekali. Cocok untuk voucher new-user/promo terbatas."
                : "Tiap user bisa pakai berkali-kali (sampai max_uses global tercapai)."}
            </p>
          </div>
          <div className="md:col-span-2 flex gap-2">
            <button data-testid="vc-submit" type="submit" className="btn-primary text-xs">Simpan Voucher</button>
            <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 border border-white/15 text-white/70 text-xs uppercase tracking-widest">Batal</button>
          </div>
        </form>
      )}

      {loading && <p className="text-white/50 text-center py-8">Memuat...</p>}
      {!loading && vouchers.length === 0 && <p className="text-white/50 text-center py-12">Belum ada voucher. Klik "Tambah Voucher" di atas.</p>}

      <div className="space-y-2">
        {vouchers.map((v) => {
          const expired = isExpired(v);
          const maxedOut = v.max_uses && v.used_count >= v.max_uses;
          const inactive = !v.active || expired || maxedOut;
          return (
            <div key={v.id} className={`border ${inactive ? "border-[#FF6B6B]/30 bg-[#FF6B6B]/5" : "border-white/10"} p-4`} data-testid={`vc-row-${v.id}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-mono font-black text-lg text-[#00F0FF]">{v.code}</span>
                    {v.per_user_limit === 1 && <span className="tag" style={{ borderColor: "rgba(255,215,0,0.6)", color: "#FFD700", background: "rgba(255,215,0,0.1)" }}>1× PER AKUN</span>}
                    {!v.active && <span className="tag" style={{ borderColor: "rgba(255,107,107,0.6)", color: "#FF6B6B", background: "rgba(255,107,107,0.1)" }}>NONAKTIF</span>}
                    {expired && <span className="tag" style={{ borderColor: "rgba(255,107,107,0.6)", color: "#FF6B6B", background: "rgba(255,107,107,0.1)" }}>EXPIRED</span>}
                    {maxedOut && <span className="tag" style={{ borderColor: "rgba(255,215,0,0.6)", color: "#FFD700", background: "rgba(255,215,0,0.1)" }}>HABIS</span>}
                  </div>
                  <p className="text-white text-sm font-bold">
                    {v.discount_type === "percentage" ? `Diskon ${v.value}%` : `Potongan ${formatIDR(v.value)}`}
                    {v.min_order_amount > 0 && <span className="text-white/50 font-normal text-xs ml-2">· min {formatIDR(v.min_order_amount)}</span>}
                  </p>
                  <p className="text-[11px] text-white/50 mt-1">
                    Pemakaian: <span className="text-white">{v.used_count || 0}</span>{v.max_uses ? <span> / {v.max_uses}</span> : ""}
                    {v.expires_at && <span className="ml-3">· Expired: {fmtDate(v.expires_at)}</span>}
                    <span className="ml-3">· Dibuat: {fmtDate(v.created_at)}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`vc-toggle-${v.id}`} onClick={() => toggleActive(v)} className={`text-xs px-3 py-2 border uppercase tracking-widest ${v.active ? "border-[#FF003C]/40 text-[#FF003C] hover:bg-[#FF003C]/10" : "border-[#00FF66]/40 text-[#00FF66] hover:bg-[#00FF66]/10"}`}>
                    {v.active ? "Nonaktif" : "Aktif"}
                  </button>
                  <button data-testid={`vc-delete-${v.id}`} onClick={() => remove(v)} className="text-white/60 hover:text-[#FF003C] p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}



function DisputesTab() {
  const [disputes, setDisputes] = useState([]);
  const [filter, setFilter] = useState("open");
  const [resolving, setResolving] = useState(null);
  const [action, setAction] = useState("release_seller");
  const [note, setNote] = useState("");

  const refresh = () => api.get(`/admin/disputes?status=${filter}`).then((r) => setDisputes(r.data));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const submit = async () => {
    try {
      await api.post(`/admin/disputes/${resolving.id}/resolve`, { action, admin_note: note });
      toast.success(action === "release_seller" ? "Dana cair ke seller" : "Refund ke buyer berhasil");
      setResolving(null);
      setNote("");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  return (
    <div data-testid="disputes-tab">
      <h2 className="font-display font-black text-2xl text-white mb-1">Komplain Order (Escrow Disputes)</h2>
      <p className="text-xs text-white/50 mb-4">Buyer mengajukan komplain — saldo escrow ditahan sampai diputuskan.</p>
      <div className="flex gap-2 mb-4">
        {[{ id: "open", label: "Open" }, { id: "all", label: "All" }].map((f) => (
          <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => setFilter(f.id)} className={`px-3 py-2 text-xs uppercase tracking-widest font-bold ${filter === f.id ? "bg-[#FF003C] text-white" : "border border-white/15 text-white/60"}`}>{f.label}</button>
        ))}
      </div>
      {disputes.length === 0 ? (
        <p className="text-white/40 text-center py-12 border border-white/10">Tidak ada komplain {filter === "open" ? "yang menunggu" : ""}.</p>
      ) : (
        <div className="space-y-3">
          {disputes.map((d) => (
            <div key={d.id} className="border border-[#FF003C]/30 bg-[#FF003C]/5 p-4" data-testid={`dispute-${d.id}`}>
              <div className="flex justify-between gap-2 flex-wrap mb-2">
                <div className="min-w-0">
                  <p className="font-mono text-xs text-white">{d.id.slice(0, 12)}</p>
                  <p className="text-[10px] text-white/50 uppercase tracking-widest">{d.buyer?.email || d.user_id}</p>
                </div>
                <span className="tag" style={{ borderColor: "#FF003C", color: "#FF003C", background: "rgba(255,0,60,0.1)" }}>{(d.delivery_status || "disputed").toUpperCase()}</span>
              </div>
              <div className="space-y-1 text-sm mb-2">
                {d.items?.map((it) => (
                  <div key={it.product_id} className="flex justify-between text-white/70">
                    <span>{it.title} × {it.quantity}</span>
                    <span>{formatIDR(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="bg-black/40 p-3 text-xs">
                <p className="text-[10px] uppercase tracking-widest text-[#FF003C] mb-1">Alasan komplain:</p>
                <p className="text-white/80">"{d.dispute_reason}"</p>
                <p className="text-[10px] text-white/40 mt-1">{new Date(d.disputed_at || d.created_at).toLocaleString("id-ID")}</p>
              </div>
              {d.delivery_status === "disputed" ? (
                <div className="mt-3 flex gap-2 flex-wrap">
                  <button data-testid={`resolve-release-${d.id}`} onClick={() => { setResolving(d); setAction("release_seller"); setNote(""); }} className="px-3 py-2 bg-[#00FF66] text-black hover:bg-[#33FF88] text-xs uppercase font-bold tracking-widest">✓ Release ke Seller</button>
                  <button data-testid={`resolve-refund-${d.id}`} onClick={() => { setResolving(d); setAction("refund_buyer"); setNote(""); }} className="px-3 py-2 bg-[#FF003C] text-white hover:bg-[#FF1A5C] text-xs uppercase font-bold tracking-widest">⤺ Refund ke Buyer</button>
                </div>
              ) : (
                <p className="text-[10px] uppercase tracking-widest text-white/40 mt-3">Sudah diputuskan: {d.release_reason || "—"}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {resolving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className={`w-full max-w-md border ${action === "release_seller" ? "border-[#00FF66]/40" : "border-[#FF003C]/40"} bg-[#0a0a0a] p-6`}>
            <h3 className="font-display font-black text-xl text-white">Konfirmasi {action === "release_seller" ? "Release Seller" : "Refund Buyer"}</h3>
            <p className="text-xs text-white/60 mt-2">Order #{resolving.id.slice(0, 12)} · Total {formatIDR(resolving.total)}</p>
            <textarea data-testid="resolve-note" rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan admin (akan terkirim ke buyer & seller via notifikasi)" className="w-full mt-3 bg-black border border-white/15 p-3 text-sm" />
            <div className="mt-4 flex gap-2">
              <button data-testid="resolve-submit" onClick={submit} className={`flex-1 px-4 py-3 ${action === "release_seller" ? "bg-[#00FF66] text-black" : "bg-[#FF003C] text-white"} font-display font-black uppercase tracking-widest text-xs`}>Konfirmasi</button>
              <button onClick={() => setResolving(null)} className="px-4 py-3 border border-white/15 text-white/70 text-xs uppercase">Batal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reviews?q=${encodeURIComponent(q)}&limit=200`);
      setReviews(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal memuat"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeReview = async (r) => {
    if (!window.confirm(`Hapus review dari ${r.user_name || "user"}?\n\n"${(r.comment || "").slice(0, 80)}..."\n\nIni akan menghapus review & menghitung ulang rating produk.`)) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Review dihapus");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  return (
    <div data-testid="reviews-tab">
      <h2 className="font-display font-black text-2xl text-white mb-1">Kelola Review Produk</h2>
      <p className="text-xs text-white/50 mb-4">Hapus review yang melanggar (spam, kata kasar, salah produk, dll). Rating produk otomatis dihitung ulang.</p>

      <div className="flex gap-2 mb-4">
        <input
          data-testid="review-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && refresh()}
          placeholder="Cari di komentar / nama user / judul produk"
          className="flex-1 bg-black border border-white/15 focus:border-[#00F0FF] text-white text-sm px-3 py-2"
        />
        <button onClick={refresh} className="btn-primary text-xs">Cari</button>
      </div>

      {loading ? (
        <p className="text-white/40 text-center py-12">Memuat...</p>
      ) : reviews.length === 0 ? (
        <p className="text-white/40 text-center py-12 border border-white/10">Tidak ada review.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="border border-white/10 p-4 flex gap-3" data-testid={`admin-review-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-bold text-white text-sm">{r.user_name || "Anonim"}</span>
                  <div className="flex">
                    {Array.from({ length: r.rating || 0 }).map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-[#FFD700] text-[#FFD700]" />
                    ))}
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-white/40">{r.created_at ? new Date(r.created_at).toLocaleDateString("id-ID") : ""}</span>
                </div>
                <p className="text-[11px] text-[#00F0FF] mb-1 truncate">Produk: {r.product_title || r.product_id}</p>
                <p className="text-white/70 text-sm whitespace-pre-wrap break-words">{r.comment}</p>
              </div>
              <button
                data-testid={`delete-review-${r.id}`}
                onClick={() => removeReview(r)}
                className="self-start inline-flex items-center gap-1 px-3 py-2 border border-[#FF003C]/40 text-[#FF003C] hover:bg-[#FF003C]/10 text-xs uppercase font-bold tracking-widest"
              >
                <Trash2 className="w-3 h-3" /> Hapus
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function WithdrawalsTab() {
  const [reqs, setReqs] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [resolving, setResolving] = useState(null); // { req, action }
  const [note, setNote] = useState("");

  const refresh = async () => {
    try {
      const { data } = await api.get(`/admin/withdrawals?status=${filter}`);
      setReqs(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };
  useEffect(() => { refresh(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (resolving.action === "reject" && note.trim().length < 5) { toast.error("Alasan reject minimal 5 karakter"); return; }
    try {
      await api.post(`/admin/withdrawals/${resolving.req.id}/${resolving.action}`, { note });
      toast.success(resolving.action === "approve" ? "Disetujui" : "Ditolak & saldo dikembalikan");
      setResolving(null);
      setNote("");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success(`${txt} disalin`);
  };

  return (
    <div data-testid="withdrawals-tab">
      <h2 className="font-display font-black text-2xl text-white mb-1">Penarikan Saldo Seller</h2>
      <p className="text-xs text-white/50 mb-4">Transfer manual ke rekening seller, lalu tandai sebagai "Selesai". Saldo seller sudah dipotong saat request masuk.</p>

      <div className="flex gap-2 mb-4">
        {[{ id: "pending", label: "Pending" }, { id: "completed", label: "Completed" }, { id: "rejected", label: "Rejected" }, { id: "all", label: "All" }].map((f) => (
          <button key={f.id} data-testid={`wd-filter-${f.id}`} onClick={() => setFilter(f.id)} className={`px-3 py-2 text-xs uppercase tracking-widest font-bold ${filter === f.id ? "bg-[#00FF66] text-black" : "border border-white/15 text-white/60"}`}>{f.label}</button>
        ))}
      </div>

      {reqs.length === 0 ? (
        <p className="text-white/40 text-center py-12 border border-white/10">Tidak ada penarikan {filter !== "all" ? filter : ""}.</p>
      ) : (
        <div className="space-y-3">
          {reqs.map((r) => {
            const statusColor = r.status === "completed" ? "#00FF66" : r.status === "rejected" ? "#FF003C" : "#FFD700";
            return (
              <div key={r.id} className="border border-white/10 p-4" data-testid={`withdrawal-${r.id}`}>
                <div className="flex justify-between items-center gap-2 flex-wrap mb-3">
                  <div className="min-w-0">
                    <p className="font-display font-black text-2xl text-[#00FF66]">{formatIDR(r.amount)}</p>
                    <p className="text-[11px] text-white/50">{r.user_name || r.user_email} ({r.user_email})</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest px-3 py-1" style={{ color: statusColor, border: `1px solid ${statusColor}`, background: `${statusColor}1a` }}>{r.status}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="border border-white/10 p-3 space-y-1">
                    <p className="text-[10px] uppercase tracking-widest text-white/40">Tujuan Transfer</p>
                    <p className="font-bold text-white flex items-center gap-2">{r.bank_name} {r.is_bca && <span className="text-[10px] bg-[#00FF66] text-black px-1 py-0.5 uppercase">Free Fee</span>}</p>
                    <p className="text-white/70 font-mono cursor-pointer hover:text-[#00F0FF]" onClick={() => copy(r.account_number)} title="Klik untuk salin">{r.account_number} 📋</p>
                    <p className="text-white/70 cursor-pointer hover:text-[#00F0FF]" onClick={() => copy(r.account_name)} title="Klik untuk salin">{r.account_name} 📋</p>
                  </div>
                  <div className="border border-white/10 p-3 space-y-1">
                    <p className="text-[10px] uppercase tracking-widest text-white/40">Perhitungan</p>
                    <div className="flex justify-between text-xs"><span className="text-white/60">Jumlah</span><span className="text-white">{formatIDR(r.amount)}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-white/60">Fee</span><span className="text-[#FFD700]">{formatIDR(r.fee)}</span></div>
                    <div className="flex justify-between text-xs font-bold border-t border-white/10 pt-1"><span className="text-white">Transfer ke seller</span><span className="text-[#00FF66] cursor-pointer hover:underline" onClick={() => copy(String(r.net_amount))}>{formatIDR(r.net_amount)} 📋</span></div>
                    <p className="text-[10px] text-white/40">{new Date(r.created_at).toLocaleString("id-ID")}</p>
                  </div>
                </div>
                {r.admin_note && <p className="text-[11px] text-[#00FF66] mt-2 italic">Catatan admin: {r.admin_note}</p>}
                {r.rejection_reason && <p className="text-[11px] text-[#FF003C] mt-2 italic">Ditolak: {r.rejection_reason}</p>}

                {r.status === "pending" && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <button data-testid={`approve-wd-${r.id}`} onClick={() => { setResolving({ req: r, action: "approve" }); setNote(""); }} className="px-3 py-2 bg-[#00FF66] text-black hover:bg-[#33FF88] text-xs uppercase font-bold tracking-widest">✓ Tandai Sudah Transfer</button>
                    <button data-testid={`reject-wd-${r.id}`} onClick={() => { setResolving({ req: r, action: "reject" }); setNote(""); }} className="px-3 py-2 bg-[#FF003C] text-white hover:bg-[#FF1A5C] text-xs uppercase font-bold tracking-widest">✗ Tolak</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {resolving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className={`w-full max-w-md border ${resolving.action === "approve" ? "border-[#00FF66]/40" : "border-[#FF003C]/40"} bg-[#0a0a0a] p-6`}>
            <h3 className="font-display font-black text-xl text-white">{resolving.action === "approve" ? "Konfirmasi: Sudah Transfer" : "Tolak Penarikan"}</h3>
            <p className="text-xs text-white/60 mt-2">{resolving.req.user_email} · {formatIDR(resolving.req.amount)} · {resolving.req.bank_name} {resolving.req.account_number}</p>
            {resolving.action === "reject" && <p className="text-xs text-[#FF003C] mt-1">⚠ Saldo seller akan dikembalikan</p>}
            <textarea data-testid="wd-note" rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder={resolving.action === "approve" ? "No. referensi transfer (opsional)" : "Alasan penolakan (min 5 karakter)"} className="w-full mt-3 bg-black border border-white/15 p-3 text-sm" />
            <div className="mt-4 flex gap-2">
              <button data-testid="wd-submit" onClick={submit} className={`flex-1 px-4 py-3 ${resolving.action === "approve" ? "bg-[#00FF66] text-black" : "bg-[#FF003C] text-white"} font-display font-black uppercase tracking-widest text-xs`}>Konfirmasi</button>
              <button onClick={() => setResolving(null)} className="px-4 py-3 border border-white/15 text-white/70 text-xs uppercase">Batal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

